// client/src/pages/admin/ManageMarketingCampaigns.js
// Admin page for the Marketing Campaign system.
// Tabs: Campaigns, Sales Leads, Lead Sources, Lead Industries,
//       Email Parts, Reply Emails, Special Access, Help

import { useState, useEffect, useCallback, useMemo } from 'react';
import marketingService from '../../services/marketingService.js';
import adminService from '../../services/adminService.js';
import Modal from '../../components/ui/Modal.js';
import Button from '../../components/ui/Button.js';

// ════════════════════════════════════════════════════════════
// MAIN PAGE COMPONENT
// ════════════════════════════════════════════════════════════

export default function ManageMarketingCampaigns() {
  const [activeTab, setActiveTab] = useState('campaigns');
  const [stats, setStats] = useState(null);
  const [showHelp, setShowHelp] = useState(false);

  useEffect(() => {
    marketingService.getStats().then(setStats).catch(() => {});
  }, [activeTab]);

  const tabs = [
    { key: 'campaigns', label: 'Campaigns', icon: '📣' },
    { key: 'leads', label: 'Sales Leads', icon: '👤' },
    { key: 'sources', label: 'Lead Sources', icon: '🔖' },
    { key: 'industries', label: 'Lead Industries', icon: '🏢' },
    { key: 'emailparts', label: 'Email Templates', icon: '✉️' },
    { key: 'replyemails', label: 'Reply Emails', icon: '↩️' },
    { key: 'access', label: 'Special Access', icon: '🔓' },
  ];

  return (
    <div className="max-w-7xl mx-auto px-4 py-8">
      <div className="flex items-center justify-between mb-6">
        <h1 className="font-display text-3xl text-gold">Marketing Campaigns</h1>
        <button
          onClick={() => setShowHelp(true)}
          className="px-4 py-2 text-sm border border-royal-600 text-royal-200 rounded-lg hover:border-gold/50 hover:text-gold transition-colors"
        >
          ? Help & Notes
        </button>
      </div>

      {/* Stats Bar */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-6 gap-3 mb-6">
          <StatCard label="Campaigns" value={stats.totalCampaigns} color="text-gold" />
          <StatCard label="Active" value={stats.activeCampaigns} color="text-green-400" />
          <StatCard label="Leads" value={stats.totalLeads} color="text-blue-400" />
          <StatCard label="Emails Sent" value={stats.totalEmailsSent} color="text-purple-400" />
          <StatCard label="Link Clicks" value={stats.totalClicks} color="text-orange-400" />
          <StatCard label="Access Grants" value={stats.activeAccessGrants} color="text-pink-400" />
        </div>
      )}

      {/* Tabs */}
      <div className="flex flex-wrap gap-2 mb-6 border-b border-royal-600 pb-3">
        {tabs.map(t => (
          <button
            key={t.key}
            onClick={() => setActiveTab(t.key)}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              activeTab === t.key
                ? 'bg-gold text-royal-900'
                : 'bg-royal-800/50 text-royal-200 border border-royal-600 hover:border-gold/50 hover:text-gold'
            }`}
          >
            {t.icon} {t.label}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      {activeTab === 'campaigns' && <CampaignsTab />}
      {activeTab === 'leads' && <LeadsTab />}
      {activeTab === 'sources' && <LeadSourcesTab />}
      {activeTab === 'industries' && <LeadIndustriesTab />}
      {activeTab === 'emailparts' && <EmailPartsTab />}
      {activeTab === 'replyemails' && <ReplyEmailsTab />}
      {activeTab === 'access' && <SpecialAccessTab />}

      {/* Help Modal */}
      <HelpModal isOpen={showHelp} onClose={() => setShowHelp(false)} />
    </div>
  );
}

function StatCard({ label, value, color }) {
  return (
    <div className="bg-royal-800/40 border border-royal-600 rounded-xl p-3 text-center">
      <p className={`text-xl font-bold ${color}`}>{value ?? '—'}</p>
      <p className="text-royal-400 text-xs">{label}</p>
    </div>
  );
}


// ════════════════════════════════════════════════════════════
// CAMPAIGNS TAB
// ════════════════════════════════════════════════════════════

function CampaignsTab() {
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [statusFilter, setStatusFilter] = useState('');
  const [showEditor, setShowEditor] = useState(false);
  const [editingCampaign, setEditingCampaign] = useState(null);
  const [showRecipients, setShowRecipients] = useState(null);
  const [showClicks, setShowClicks] = useState(null);

  const loadCampaigns = useCallback(() => {
    setLoading(true);
    marketingService.getCampaigns({ page, status: statusFilter || undefined })
      .then(d => {
        setCampaigns(d.campaigns || []);
        setTotalPages(d.totalPages || 1);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [page, statusFilter]);

  useEffect(() => { loadCampaigns(); }, [loadCampaigns]);

  const handleAction = async (id, action) => {
    try {
      if (action === 'execute') {
        if (!window.confirm('Execute this campaign? This will build the recipient list and begin sending.')) return;
        await marketingService.executeCampaign(id);
      } else if (action === 'pause') {
        await marketingService.pauseCampaign(id);
      } else if (action === 'resume') {
        await marketingService.resumeCampaign(id);
      } else if (action === 'cancel') {
        if (!window.confirm('Cancel this campaign?')) return;
        await marketingService.cancelCampaign(id);
      } else if (action === 'delete') {
        if (!window.confirm('Delete this campaign permanently?')) return;
        await marketingService.deleteCampaign(id);
      }
      loadCampaigns();
    } catch (err) {
      alert(err.data?.error || 'Action failed');
    }
  };

  const statusColors = {
    draft: 'text-royal-200 bg-royal-800/60',
    scheduled: 'text-blue-400 bg-blue-900/30',
    running: 'text-green-400 bg-green-900/30',
    paused: 'text-yellow-400 bg-yellow-900/30',
    completed: 'text-emerald-400 bg-emerald-900/30',
    cancelled: 'text-red-400 bg-red-900/30',
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <select
            value={statusFilter}
            onChange={e => { setStatusFilter(e.target.value); setPage(1); }}
            className="px-3 py-2 bg-royal-800/60 border border-royal-600 rounded-lg text-sm text-white"
          >
            <option value="">All Statuses</option>
            <option value="draft">Draft</option>
            <option value="scheduled">Scheduled</option>
            <option value="running">Running</option>
            <option value="paused">Paused</option>
            <option value="completed">Completed</option>
            <option value="cancelled">Cancelled</option>
          </select>
        </div>
        <Button onClick={() => { setEditingCampaign(null); setShowEditor(true); }}>
          + New Campaign
        </Button>
      </div>

      {loading ? (
        <LoadingSpinner />
      ) : campaigns.length === 0 ? (
        <EmptyState message="No campaigns yet. Create your first campaign to get started." />
      ) : (
        <div className="space-y-3">
          {campaigns.map(c => (
            <div key={c.campaignid} className="bg-royal-800/40 border border-royal-600 rounded-xl p-4">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-1">
                    <h3 className="text-white font-semibold">{c.name}</h3>
                    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${statusColors[c.status] || ''}`}>
                      {c.status}
                    </span>
                    <span className="text-royal-400 text-xs">{c.campaign_type}</span>
                  </div>
                  {c.description && <p className="text-royal-300 text-sm mb-2">{c.description}</p>}
                  <div className="flex items-center gap-4 text-xs text-royal-400">
                    <span>Source: {c.audience_source}</span>
                    <span>Recipients: {c.recipient_count || 0}</span>
                    <span>Sent: {c.sent_count || 0}</span>
                    <span>Emails: {c.email_count || 0}</span>
                    {c.sending_domain && <span className="text-blue-400">📤 {c.sending_domain}</span>}
                    {c.schedule_type === 'triggered' && <span className="text-purple-400">⚡ Triggered</span>}
                    {c.scheduled_at && c.schedule_type === 'scheduled' && <span>Scheduled: {new Date(c.scheduled_at).toLocaleString()}</span>}
                    {c.triggered_end_at && c.schedule_type === 'triggered' && <span>Ends: {new Date(c.triggered_end_at).toLocaleDateString()}</span>}
                  </div>
                </div>
                <div className="flex items-center gap-2 ml-4">
                  {c.status === 'draft' && (
                    <>
                      <SmallBtn onClick={() => { setEditingCampaign(c); setShowEditor(true); }} label="Edit" />
                      <SmallBtn onClick={() => handleAction(c.campaignid, 'execute')} label="Execute" variant="green" />
                      <SmallBtn onClick={() => handleAction(c.campaignid, 'delete')} label="Delete" variant="red" />
                    </>
                  )}
                  {c.status === 'scheduled' && (
                    <SmallBtn onClick={() => handleAction(c.campaignid, 'cancel')} label="Cancel" variant="red" />
                  )}
                  {c.status === 'running' && (
                    <SmallBtn onClick={() => handleAction(c.campaignid, 'pause')} label="Pause" variant="yellow" />
                  )}
                  {c.status === 'paused' && (
                    <>
                      <SmallBtn onClick={() => handleAction(c.campaignid, 'resume')} label="Resume" variant="green" />
                      <SmallBtn onClick={() => { setEditingCampaign(c); setShowEditor(true); }} label="Edit" />
                      <SmallBtn onClick={() => handleAction(c.campaignid, 'execute')} label="Re-execute" variant="green" />
                    </>
                  )}
                  {(c.recipient_count > 0) && (
                    <>
                      <SmallBtn onClick={() => setShowRecipients(c.campaignid)} label="Recipients" />
                      <SmallBtn onClick={() => setShowClicks(c.campaignid)} label="Clicks" />
                    </>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <SimplePagination page={page} totalPages={totalPages} onPageChange={setPage} />

      {showEditor && (
        <CampaignEditorModal
          campaign={editingCampaign}
          onClose={() => { setShowEditor(false); setEditingCampaign(null); }}
          onSaved={() => { setShowEditor(false); setEditingCampaign(null); loadCampaigns(); }}
        />
      )}

      {showRecipients && (
        <RecipientsModal
          campaignId={showRecipients}
          onClose={() => setShowRecipients(null)}
        />
      )}

      {showClicks && (
        <ClickAnalyticsModal
          campaignId={showClicks}
          onClose={() => setShowClicks(null)}
        />
      )}
    </div>
  );
}


// ════════════════════════════════════════════════════════════
// CAMPAIGN VALIDATION
// ════════════════════════════════════════════════════════════
//
// Live validation gating the Create/Update button. Returns
// per-step errors so each step tab can show a red dot when
// something on that step is incomplete.
//
// Mirrors validateCampaignPayload() on the server. Server is the
// source of truth — this is just so the UI can disable the button
// and surface friendly errors before submission.

function validateCampaign({ form, audienceFilters, emails }) {
  const errors = { basics: [], audience: [], emailReward: [], review: [] };

  // ── Basics ──
  if (!form.name || !form.name.trim()) {
    errors.basics.push('Campaign name is required');
  }
  if (form.schedule_type === 'scheduled' && !form.scheduled_at) {
    errors.basics.push('Pick a date and time for the scheduled send');
  }

  // ── Audience ──
  const groups = audienceFilters?.groups || [];
  const manual = audienceFilters?.manual || {};
  const wantsUsers = form.audience_source === 'users' || form.audience_source === 'both';
  const wantsLeads = form.audience_source === 'sales_leads' || form.audience_source === 'both';

  const hasCondition = groups.some(g =>
    Array.isArray(g.conditions) &&
    g.conditions.some(c => c.table && c.column && c.operator)
  );
  const hasManualUsers = wantsUsers && Array.isArray(manual.userids) && manual.userids.length > 0;
  const hasManualLeads = wantsLeads && Array.isArray(manual.salesleadids) && manual.salesleadids.length > 0;

  if (!hasCondition && !hasManualUsers && !hasManualLeads) {
    errors.audience.push('Add at least one condition or manually select recipients');
  }

  // ── Email & Reward ──
  if (!emails.length) {
    errors.emailReward.push('At least one email is required');
  } else {
    const first = emails[0];
    if (!first.email_subject || !first.email_subject.trim()) {
      errors.emailReward.push('Email subject is required');
    }
    if (!first.email_html_content || !first.email_html_content.trim()) {
      errors.emailReward.push('Email body is required');
    }
  }

  const ct = form.campaign_type || 'informational';
  const rc = form.reward_config || {};

  if (ct === 'reward_text') {
    if (!rc.reward_text || !String(rc.reward_text).trim()) {
      errors.emailReward.push('Reward text is required');
    }
  } else if (ct === 'reward_access') {
    if (!Array.isArray(rc.template_ids) || rc.template_ids.length === 0) {
      errors.emailReward.push('Select at least one eCard template for the reward');
    }
    if (!rc.access_name || !String(rc.access_name).trim()) {
      errors.emailReward.push('Access name is required');
    }
  } else if (ct === 'reward_giftcode') {
    if (!form.gift_usertypeid) {
      errors.emailReward.push('Select a gift plan');
    }
  }

  const valid = Object.values(errors).every(arr => arr.length === 0);
  return { valid, errors };
}


// ════════════════════════════════════════════════════════════
// CAMPAIGN EDITOR MODAL
// ════════════════════════════════════════════════════════════

function CampaignEditorModal({ campaign, onClose, onSaved }) {
  const isEdit = !!campaign;

  const [form, setForm] = useState({
    name: campaign?.name || '',
    description: campaign?.description || '',
    audience_source: campaign?.audience_source || 'users',
    campaign_type: campaign?.campaign_type || 'informational',
    schedule_type: campaign?.schedule_type || 'immediate',
    scheduled_at: campaign?.scheduled_at ? campaign.scheduled_at.slice(0, 16) : '',
    triggered_start_at: campaign?.triggered_start_at ? campaign.triggered_start_at.slice(0, 16) : '',
    triggered_end_at: campaign?.triggered_end_at ? campaign.triggered_end_at.slice(0, 16) : '',
    reward_templateid: campaign?.reward_templateid || '',
    reward_config: campaign?.reward_config || {},
    gift_usertypeid: campaign?.gift_usertypeid || '',
    gift_interval: campaign?.gift_interval || 'yearly',
    gift_duration_days: campaign?.gift_duration_days || 365,
    sending_domain_id: campaign?.sending_domain_id || '',
    reply_email_id: campaign?.reply_email_id || '',
  });

  // Initialize audience filters with both groups (conditions) and manual.
  // Manual picks live alongside groups in the same JSONB blob, no schema change.
  const [audienceFilters, setAudienceFilters] = useState(() => {
    const af = campaign?.audience_filters || {};
    return {
      logic: af.logic || 'OR',
      groups: af.groups && af.groups.length
        ? af.groups
        : [{ logic: 'AND', conditions: [] }],
      manual: {
        userids: af.manual?.userids || [],
        salesleadids: af.manual?.salesleadids || [],
      },
    };
  });

  const [emails, setEmails] = useState([]);
  const [rewardTemplates, setRewardTemplates] = useState([]);
  const [plans, setPlans] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [sendingDomains, setSendingDomains] = useState([]);
  const [replyEmails, setReplyEmails] = useState([]);
  const [audiencePreview, setAudiencePreview] = useState(null);
  const [saving, setSaving] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [editorStep, setEditorStep] = useState('basics');
  const [bodyTemplates, setBodyTemplates] = useState([]);

  // Load campaign emails if editing
  useEffect(() => {
    if (isEdit) {
      marketingService.getCampaign(campaign.campaignid).then(d => {
        setEmails(d.emails || [{ email_subject: '', email_html_content: '', delay_days: 0 }]);
      }).catch(() => {
        setEmails([{ email_subject: '', email_html_content: '', delay_days: 0 }]);
      });
    } else {
      setEmails([{ email_subject: '', email_html_content: '', delay_days: 0 }]);
    }
  }, [isEdit, campaign]);

  // Load lookups
  useEffect(() => {
    marketingService.getRewardTemplates().then(d => setRewardTemplates(d.rewardTemplates || [])).catch(() => {});
    marketingService.getAvailablePlans().then(d => setPlans(d.plans || [])).catch(() => {});
    marketingService.getAvailableTemplates().then(d => setTemplates(d.templates || [])).catch(() => {});
    marketingService.getBodyTemplates().then(d => setBodyTemplates(d.bodyTemplates || [])).catch(() => {});
    marketingService.getSendingDomains().then(d => setSendingDomains(d.sendingDomains || [])).catch(() => {});
    marketingService.getReplyEmails().then(d => setReplyEmails(d.replyEmails || [])).catch(() => {});
  }, []);

  const updateForm = (key, val) => setForm(prev => ({ ...prev, [key]: val }));
  const updateRewardConfig = (key, val) => setForm(prev => ({
    ...prev,
    reward_config: { ...prev.reward_config, [key]: val }
  }));

  const updateEmail = (idx, key, val) => {
    setEmails(prev => prev.map((e, i) => i === idx ? { ...e, [key]: val } : e));
  };

  const addEmail = () => {
    setEmails(prev => [...prev, { email_subject: '', email_html_content: '', delay_days: 0 }]);
  };

  const removeEmail = (idx) => {
    if (emails.length <= 1) return;
    setEmails(prev => prev.filter((_, i) => i !== idx));
  };

  const handlePreviewAudience = async () => {
    setPreviewLoading(true);
    try {
      const result = await marketingService.previewAudience({
        audience_source: form.audience_source,
        audience_filters: audienceFilters,
      });
      setAudiencePreview(result);
    } catch (err) {
      alert(err.data?.error || 'Preview failed');
    }
    setPreviewLoading(false);
  };

  // Live validation — recomputes whenever any input changes.
  const validation = useMemo(
    () => validateCampaign({ form, audienceFilters, emails }),
    [form, audienceFilters, emails]
  );

  const handleSave = async () => {
    if (!validation.valid) return;

    setSaving(true);
    try {
      const payload = {
        ...form,
        audience_filters: audienceFilters,
        scheduled_at: form.schedule_type === 'scheduled' && form.scheduled_at ? form.scheduled_at : null,
        triggered_start_at: form.schedule_type === 'triggered' && form.triggered_start_at ? form.triggered_start_at : null,
        triggered_end_at: form.schedule_type === 'triggered' && form.triggered_end_at ? form.triggered_end_at : null,
        reward_templateid: form.reward_templateid || null,
        sending_domain_id: form.sending_domain_id || null,
        reply_email_id: form.reply_email_id || null,
        gift_usertypeid: form.campaign_type === 'reward_giftcode' ? (form.gift_usertypeid || null) : null,
        gift_interval: form.campaign_type === 'reward_giftcode' ? form.gift_interval : null,
        gift_duration_days: form.campaign_type === 'reward_giftcode' ? form.gift_duration_days : null,
        emails: emails.map((e) => ({
          email_subject: e.email_subject,
          email_html_content: e.email_html_content,
          delay_days: e.delay_days || 0,
          reward_templateid: e.reward_templateid || null,
        })),
      };

      if (isEdit) {
        await marketingService.updateCampaign(campaign.campaignid, payload);
      } else {
        await marketingService.createCampaign(payload);
      }
      onSaved();
    } catch (err) {
      alert(err.data?.error || 'Save failed');
    }
    setSaving(false);
  };

  const steps = [
    { key: 'basics', label: '1. Basics' },
    { key: 'audience', label: '2. Audience' },
    { key: 'emailReward', label: '3. Email & Reward' },
    { key: 'review', label: '4. Review' },
  ];

  return (
    <Modal isOpen={true} onClose={onClose} title={isEdit ? 'Edit Campaign' : 'New Campaign'} wide>
      <div className="flex flex-col max-h-[80vh] -mx-6 -my-4">

        {/* FIXED: Step Navigation */}
        <div className="flex-shrink-0 flex flex-wrap gap-2 px-6 pt-4 pb-3 border-b border-royal-600">
          {steps.map(s => {
            const hasErrors = validation.errors[s.key]?.length > 0;
            return (
              <button
                key={s.key}
                onClick={() => setEditorStep(s.key)}
                className={`relative px-3 py-1.5 rounded text-xs font-medium transition-colors ${
                  editorStep === s.key
                    ? 'bg-gold text-royal-900'
                    : 'text-royal-300 hover:text-gold'
                }`}
              >
                {s.label}
                {hasErrors && (
                  <span className="absolute -top-1 -right-1 w-2 h-2 bg-red-500 rounded-full" title="Step has errors" />
                )}
              </button>
            );
          })}
        </div>

        {/* SCROLLING: Step Body */}
        <div className="flex-1 min-h-0 overflow-y-auto px-6 py-4">

          {editorStep === 'basics' && (
            <BasicsStep
              form={form}
              updateForm={updateForm}
              sendingDomains={sendingDomains}
              replyEmails={replyEmails}
            />
          )}

          {editorStep === 'audience' && (
            <AudienceStep
              audienceFilters={audienceFilters}
              setAudienceFilters={setAudienceFilters}
              audienceSource={form.audience_source}
              scheduleType={form.schedule_type}
              audiencePreview={audiencePreview}
              previewLoading={previewLoading}
              onPreview={handlePreviewAudience}
            />
          )}

          {editorStep === 'emailReward' && (
            <EmailAndRewardStep
              form={form}
              updateForm={updateForm}
              updateRewardConfig={updateRewardConfig}
              emails={emails}
              updateEmail={updateEmail}
              addEmail={addEmail}
              removeEmail={removeEmail}
              bodyTemplates={bodyTemplates}
              rewardTemplates={rewardTemplates}
              templates={templates}
              plans={plans}
            />
          )}

          {editorStep === 'review' && (
            <ReviewStep
              form={form}
              audienceFilters={audienceFilters}
              emails={emails}
              plans={plans}
              validation={validation}
            />
          )}

        </div>

        {/* FIXED: Footer */}
        <div className="flex-shrink-0 flex items-center justify-between px-6 pt-4 pb-2 border-t border-royal-600">
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <div className="flex items-center gap-3">
            {!validation.valid && (
              <span className="text-xs text-red-400">
                Complete required fields to {isEdit ? 'update' : 'create'}
              </span>
            )}
            <Button onClick={handleSave} disabled={saving || !validation.valid}>
              {saving ? 'Saving...' : isEdit ? 'Update Campaign' : 'Create Campaign'}
            </Button>
          </div>
        </div>
      </div>
    </Modal>
  );
}


// ────────────────────────────────────────────────────────────
// STEP 1: BASICS
// ────────────────────────────────────────────────────────────

function BasicsStep({ form, updateForm, sendingDomains, replyEmails }) {
  return (
    <div className="space-y-4">
      <FormField label="Campaign Name *">
        <input
          value={form.name}
          onChange={e => updateForm('name', e.target.value)}
          className="w-full px-3 py-2 bg-royal-800/60 border border-royal-600 rounded-lg text-white text-sm"
          placeholder="e.g., Holiday Special Access 2026"
        />
      </FormField>

      <FormField label="Description">
        <textarea
          value={form.description}
          onChange={e => updateForm('description', e.target.value)}
          rows={2}
          className="w-full px-3 py-2 bg-royal-800/60 border border-royal-600 rounded-lg text-white text-sm"
          placeholder="Internal notes about this campaign..."
        />
      </FormField>

      <div className="grid grid-cols-2 gap-4">
        <FormField label="Audience Source">
          <select
            value={form.audience_source}
            onChange={e => updateForm('audience_source', e.target.value)}
            className="w-full px-3 py-2 bg-royal-800/60 border border-royal-600 rounded-lg text-white text-sm"
          >
            <option value="users">Users Only</option>
            <option value="sales_leads">Sales Leads Only</option>
            <option value="both">Users + Sales Leads</option>
          </select>
        </FormField>

        <FormField label="Campaign Type">
          <select
            value={form.campaign_type}
            onChange={e => updateForm('campaign_type', e.target.value)}
            className="w-full px-3 py-2 bg-royal-800/60 border border-royal-600 rounded-lg text-white text-sm"
          >
            <option value="informational">Informational (no reward)</option>
            <option value="reward_text">Reward: Text</option>
            <option value="reward_access">Reward: Special Access</option>
            <option value="reward_giftcode">Reward: Gift Code</option>
          </select>
        </FormField>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <FormField label="Schedule">
          <select
            value={form.schedule_type}
            onChange={e => updateForm('schedule_type', e.target.value)}
            className="w-full px-3 py-2 bg-royal-800/60 border border-royal-600 rounded-lg text-white text-sm"
          >
            <option value="immediate">Send Now (on execute)</option>
            <option value="scheduled">Send Later (specific date)</option>
            <option value="series">Drip Series (multiple emails over time)</option>
            <option value="triggered">Triggered / Automated (ongoing)</option>
          </select>
        </FormField>

        {form.schedule_type === 'scheduled' && (
          <FormField label="Send At *">
            <input
              type="datetime-local"
              value={form.scheduled_at}
              onChange={e => updateForm('scheduled_at', e.target.value)}
              className="w-full px-3 py-2 bg-royal-800/60 border border-royal-600 rounded-lg text-white text-sm"
            />
          </FormField>
        )}
      </div>

      {form.schedule_type === 'triggered' && (
        <div className="bg-royal-800/40 border border-purple-500/30 rounded-lg p-4 space-y-3">
          <p className="text-purple-300 text-xs font-medium uppercase tracking-wider">Triggered Campaign Settings</p>
          <p className="text-royal-300 text-xs leading-relaxed">
            The system checks every hour for people who newly match your audience filter and automatically sends them the email.
            Each person only receives it once. Great for welcome emails, milestone rewards, and re-engagement flows.
          </p>
          <div className="grid grid-cols-2 gap-4">
            <FormField label="Start Watching">
              <input
                type="datetime-local"
                value={form.triggered_start_at}
                onChange={e => updateForm('triggered_start_at', e.target.value)}
                className="w-full px-3 py-2 bg-royal-800/60 border border-royal-600 rounded-lg text-white text-sm"
              />
              <p className="text-royal-400 text-xs mt-1">Only people created/modified after this date will be included. Leave blank to include everyone who matches now.</p>
            </FormField>

            <FormField label="Stop Watching">
              <input
                type="datetime-local"
                value={form.triggered_end_at}
                onChange={e => updateForm('triggered_end_at', e.target.value)}
                className="w-full px-3 py-2 bg-royal-800/60 border border-royal-600 rounded-lg text-white text-sm"
              />
              <p className="text-royal-400 text-xs mt-1">Campaign completes automatically on this date. Leave blank to run until manually cancelled.</p>
            </FormField>
          </div>
        </div>
      )}

      <FormField label="Sending Domain">
        <select
          value={form.sending_domain_id}
          onChange={e => updateForm('sending_domain_id', e.target.value)}
          className="w-full px-3 py-2 bg-royal-800/60 border border-royal-600 rounded-lg text-white text-sm"
        >
          <option value="">Use Default / Fallback Domain</option>
          {sendingDomains.map(d => (
            <option key={d.domainid} value={d.domainid}>
              {d.domain} — {d.from_email}
              {d.status === 'warming' ? ' 🔥' : ''}
              {d.warmup_phase < 4 ? ` (Phase ${d.warmup_phase})` : ''}
            </option>
          ))}
        </select>
        <p className="text-royal-400 text-xs mt-1">
          Campaign emails will be sent from this domain. Useful for domain warmup or reputation isolation.
        </p>
      </FormField>

      <FormField label="Reply-To Email">
        <select
          value={form.reply_email_id}
          onChange={e => updateForm('reply_email_id', e.target.value)}
          className="w-full px-3 py-2 bg-royal-800/60 border border-royal-600 rounded-lg text-white text-sm"
        >
          <option value="">Use Admin Email (fallback)</option>
          {replyEmails.map(r => (
            <option key={r.replyemailid} value={r.replyemailid}>
              {r.email}{r.display_name ? ` (${r.display_name})` : ''}{r.is_default ? ' ★' : ''}
            </option>
          ))}
        </select>
        <p className="text-royal-400 text-xs mt-1">
          When recipients reply to campaign emails, their replies go to this address.
        </p>
      </FormField>
    </div>
  );
}


// ────────────────────────────────────────────────────────────
// STEP 2: AUDIENCE
// ────────────────────────────────────────────────────────────

function AudienceStep({
  audienceFilters, setAudienceFilters, audienceSource, scheduleType,
  audiencePreview, previewLoading, onPreview,
}) {
  const isTriggered = scheduleType === 'triggered';

  return (
    <div className="space-y-6">
      {/* Conditions section */}
      <div>
        <h3 className="text-gold text-sm font-medium mb-2">Filter by Conditions</h3>
        <AudienceBuilder
          filters={audienceFilters}
          onChange={setAudienceFilters}
          audienceSource={audienceSource}
        />
      </div>

      {/* Manual selection section — disabled note for triggered */}
      <div>
        <h3 className="text-gold text-sm font-medium mb-2">Manual Selection</h3>
        {isTriggered ? (
          <div className="bg-royal-800/40 border border-purple-500/30 rounded-lg p-4">
            <p className="text-purple-300 text-xs font-medium uppercase tracking-wider mb-1">
              ⚡ Manual Selection Disabled for Triggered Campaigns
            </p>
            <p className="text-royal-300 text-xs leading-relaxed">
              Triggered campaigns auto-detect new matching recipients every hour using your conditions filter.
              Manually picking specific people doesn't apply to this mode. To send to specific people once,
              use a "Send Now" or "Send Later" schedule type instead.
            </p>
          </div>
        ) : (
          <ManualSelectionPanel
            audienceFilters={audienceFilters}
            setAudienceFilters={setAudienceFilters}
            audienceSource={audienceSource}
          />
        )}
      </div>

      {/* Preview */}
      <div>
        <div className="flex items-center gap-3 mb-3">
          <Button variant="outline" onClick={onPreview} disabled={previewLoading}>
            {previewLoading ? 'Loading...' : 'Preview Audience'}
          </Button>
          <p className="text-royal-400 text-xs">Combines conditions + manual picks (deduped). Unsubscribed are excluded.</p>
        </div>

        {audiencePreview && (
          <div className="bg-royal-800/60 border border-royal-600 rounded-lg p-4">
            <p className="text-white text-sm font-medium mb-2">
              {audiencePreview.totalCount} recipient(s) match
              {audiencePreview.excludedCount > 0 && (
                <span className="text-red-400 ml-2">({audiencePreview.excludedCount} excluded — unsubscribed)</span>
              )}
            </p>
            {audiencePreview.users && (
              <p className="text-royal-300 text-xs">Users: {audiencePreview.users.total} active, {audiencePreview.users.excluded} excluded</p>
            )}
            {audiencePreview.salesLeads && (
              <p className="text-royal-300 text-xs">Sales Leads: {audiencePreview.salesLeads.total} active, {audiencePreview.salesLeads.excluded} excluded</p>
            )}
            {audiencePreview.sample?.length > 0 && (
              <div className="mt-3 space-y-1">
                <p className="text-royal-400 text-xs uppercase">Sample:</p>
                {audiencePreview.sample.map((s, i) => (
                  <p key={i} className="text-white text-xs">
                    <span className="text-royal-400">{s.type === 'user' ? '👤' : '📋'}</span>{' '}
                    {s.name} — <span className="text-royal-300">{s.email}</span>
                  </p>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}


// ────────────────────────────────────────────────────────────
// MANUAL SELECTION PANEL
// ────────────────────────────────────────────────────────────

function ManualSelectionPanel({ audienceFilters, setAudienceFilters, audienceSource }) {
  const userIds = audienceFilters.manual?.userids || [];
  const leadIds = audienceFilters.manual?.salesleadids || [];

  const [pickedUsers, setPickedUsers] = useState([]);
  const [pickedLeads, setPickedLeads] = useState([]);
  const [openPicker, setOpenPicker] = useState(null);

  useEffect(() => {
    if (!userIds.length && !leadIds.length) {
      setPickedUsers([]);
      setPickedLeads([]);
      return;
    }
    marketingService.lookupPickedRecipients({ userids: userIds, salesleadids: leadIds })
      .then(d => {
        setPickedUsers(d.users || []);
        setPickedLeads(d.leads || []);
      })
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userIds.join(','), leadIds.join(',')]);

  const wantsUsers = audienceSource === 'users' || audienceSource === 'both';
  const wantsLeads = audienceSource === 'sales_leads' || audienceSource === 'both';

  const setUserIds = (next) => setAudienceFilters({
    ...audienceFilters,
    manual: { ...audienceFilters.manual, userids: next },
  });

  const setLeadIds = (next) => setAudienceFilters({
    ...audienceFilters,
    manual: { ...audienceFilters.manual, salesleadids: next },
  });

  return (
    <div className="bg-royal-800/30 border border-royal-600 rounded-lg p-4 space-y-4">
      <p className="text-royal-300 text-xs">
        Pick specific recipients in addition to (or instead of) the conditions above. These are <strong className="text-gold">merged</strong> with condition matches and deduplicated. Unsubscribed people are still excluded automatically.
      </p>

      {wantsUsers && (
        <div>
          <div className="flex items-center justify-between mb-2">
            <span className="text-white text-sm font-medium">
              Picked Users <span className="text-royal-400 font-normal">({userIds.length})</span>
            </span>
            <button
              onClick={() => setOpenPicker('users')}
              className="text-gold text-xs hover:text-gold-light transition-colors"
            >
              + Pick Users
            </button>
          </div>
          {userIds.length > 0 && (
            <ChipList
              items={pickedUsers.map(u => ({
                id: u.userid,
                label: [u.firstname, u.lastname].filter(Boolean).join(' ') || u.email,
                sublabel: u.email,
                warning: u.marketing_emails_unsubscribe ? 'unsubscribed' : null,
              }))}
              onRemove={(id) => setUserIds(userIds.filter(uid => uid !== id))}
            />
          )}
        </div>
      )}

      {wantsLeads && (
        <div>
          <div className="flex items-center justify-between mb-2">
            <span className="text-white text-sm font-medium">
              Picked Sales Leads <span className="text-royal-400 font-normal">({leadIds.length})</span>
            </span>
            <button
              onClick={() => setOpenPicker('leads')}
              className="text-gold text-xs hover:text-gold-light transition-colors"
            >
              + Pick Sales Leads
            </button>
          </div>
          {leadIds.length > 0 && (
            <ChipList
              items={pickedLeads.map(l => ({
                id: l.salesleadid,
                label: [l.firstname, l.lastname].filter(Boolean).join(' ') || l.email,
                sublabel: l.email,
                warning: l.unsubscribed ? 'unsubscribed' : null,
              }))}
              onRemove={(id) => setLeadIds(leadIds.filter(lid => lid !== id))}
            />
          )}
        </div>
      )}

      {openPicker === 'users' && (
        <ManualPickerModal
          kind="users"
          selectedIds={userIds}
          onConfirm={(ids) => { setUserIds(ids); setOpenPicker(null); }}
          onClose={() => setOpenPicker(null)}
        />
      )}
      {openPicker === 'leads' && (
        <ManualPickerModal
          kind="leads"
          selectedIds={leadIds}
          onConfirm={(ids) => { setLeadIds(ids); setOpenPicker(null); }}
          onClose={() => setOpenPicker(null)}
        />
      )}
    </div>
  );
}

function ChipList({ items, onRemove }) {
  if (!items.length) return null;
  return (
    <div className="flex flex-wrap gap-1.5">
      {items.map(item => (
        <div
          key={item.id}
          className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-full text-xs border ${
            item.warning
              ? 'bg-red-900/30 border-red-700 text-red-200'
              : 'bg-royal-700/60 border-royal-500 text-white'
          }`}
          title={item.sublabel + (item.warning ? ` — ${item.warning}` : '')}
        >
          <span>{item.label}</span>
          {item.warning && <span className="text-red-400">⚠</span>}
          <button
            onClick={() => onRemove(item.id)}
            className="text-royal-300 hover:text-white ml-0.5"
            aria-label="Remove"
          >
            ✕
          </button>
        </div>
      ))}
    </div>
  );
}


// ────────────────────────────────────────────────────────────
// MANUAL PICKER MODAL
// ────────────────────────────────────────────────────────────

function ManualPickerModal({ kind, selectedIds, onConfirm, onClose }) {
  const isUsers = kind === 'users';
  const [items, setItems] = useState([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [loading, setLoading] = useState(true);
  const [picked, setPicked] = useState(new Set(selectedIds));

  const idKey = isUsers ? 'userid' : 'salesleadid';
  const unsubKey = isUsers ? 'marketing_emails_unsubscribe' : 'unsubscribed';

  const load = useCallback(() => {
    setLoading(true);
    const fetcher = isUsers
      ? marketingService.getUsersForPicker
      : marketingService.getLeadsForPicker;

    fetcher({ page, limit: 50, search: search || undefined })
      .then(d => {
        setItems(isUsers ? (d.users || []) : (d.leads || []));
        setTotalPages(d.totalPages || 1);
        setTotal(d.total || 0);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [isUsers, page, search]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    const t = setTimeout(() => {
      setSearch(searchInput);
      setPage(1);
    }, 300);
    return () => clearTimeout(t);
  }, [searchInput]);

  const togglePick = (id) => {
    setPicked(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const allOnPagePicked = items.length > 0 && items.every(it => picked.has(it[idKey]));

  const togglePageAll = () => {
    setPicked(prev => {
      const next = new Set(prev);
      if (allOnPagePicked) {
        items.forEach(it => next.delete(it[idKey]));
      } else {
        items.forEach(it => next.add(it[idKey]));
      }
      return next;
    });
  };

  return (
    <Modal isOpen={true} onClose={onClose} title={isUsers ? 'Pick Users' : 'Pick Sales Leads'} wide>
      <div className="flex flex-col max-h-[75vh] -mx-6 -my-4">

        <div className="flex-shrink-0 px-6 pt-4 pb-3 border-b border-royal-600 space-y-3">
          <input
            value={searchInput}
            onChange={e => setSearchInput(e.target.value)}
            placeholder="Search by email, first name, or last name..."
            className="w-full px-3 py-2 bg-royal-800/60 border border-royal-600 rounded-lg text-sm text-white"
          />
          <div className="flex items-center justify-between text-xs">
            <span className="text-royal-300">
              {picked.size} selected · {total} matching
            </span>
            <button
              onClick={togglePageAll}
              className="text-gold hover:text-gold-light"
            >
              {allOnPagePicked ? 'Unselect this page' : 'Select all on this page'}
            </button>
          </div>
        </div>

        <div className="flex-1 min-h-0 overflow-y-auto px-6 py-3">
          {loading ? (
            <LoadingSpinner />
          ) : items.length === 0 ? (
            <EmptyState message={search ? 'No matches.' : `No ${isUsers ? 'users' : 'leads'} yet.`} />
          ) : (
            <div className="space-y-1">
              {items.map(it => {
                const id = it[idKey];
                const isPicked = picked.has(id);
                const isUnsub = it[unsubKey];
                const name = [it.firstname, it.lastname].filter(Boolean).join(' ');

                return (
                  <label
                    key={id}
                    className={`flex items-center gap-3 px-3 py-2 rounded cursor-pointer text-sm transition-colors ${
                      isPicked ? 'bg-gold/10 border border-gold/30' : 'hover:bg-royal-800/40 border border-transparent'
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={isPicked}
                      onChange={() => togglePick(id)}
                      className="rounded"
                    />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="text-white font-medium truncate">
                          {name || it.email}
                        </span>
                        {isUnsub && (
                          <span className="text-red-400 text-xs whitespace-nowrap" title="Unsubscribed — will be skipped at send time">
                            ⚠ unsubscribed
                          </span>
                        )}
                      </div>
                      <p className="text-royal-300 text-xs truncate">
                        {it.email}
                        {!isUsers && it.businessname ? ` · ${it.businessname}` : ''}
                        {isUsers && it.typename ? ` · ${it.typename}` : ''}
                      </p>
                    </div>
                  </label>
                );
              })}
            </div>
          )}
        </div>

        <div className="flex-shrink-0 px-6 pt-3 pb-2 border-t border-royal-600">
          <SimplePagination page={page} totalPages={totalPages} onPageChange={setPage} />
          <div className="flex items-center justify-between mt-3">
            <Button variant="ghost" onClick={onClose}>Cancel</Button>
            <Button onClick={() => onConfirm([...picked])}>
              Confirm Selection ({picked.size})
            </Button>
          </div>
        </div>

      </div>
    </Modal>
  );
}


// ────────────────────────────────────────────────────────────
// STEP 3: EMAIL & REWARD (merged)
// ────────────────────────────────────────────────────────────

function EmailAndRewardStep({
  form, updateForm, updateRewardConfig,
  emails, updateEmail, addEmail, removeEmail,
  bodyTemplates, rewardTemplates, templates, plans,
}) {
  const filteredRewardTemplates = rewardTemplates.filter(rt => {
    if (form.campaign_type === 'reward_text') return rt.reward_type === 'text';
    if (form.campaign_type === 'reward_access') return rt.reward_type === 'access';
    if (form.campaign_type === 'reward_giftcode') return rt.reward_type === 'giftcode';
    return false;
  });

  const isReward = form.campaign_type !== 'informational';

  return (
    <div className="space-y-5">

      {/* Email composition */}
      <div>
        <h3 className="text-gold text-sm font-medium mb-3">Email Content</h3>
        {emails.map((email, idx) => (
          <div key={idx} className="bg-royal-800/40 border border-royal-600 rounded-lg p-4 mb-3">
            <div className="flex items-center justify-between mb-3">
              <h4 className="text-white text-sm font-medium">
                Email {idx + 1}{form.schedule_type === 'series' && idx > 0 ? ` (delay: ${email.delay_days} days)` : ''}
              </h4>
              {emails.length > 1 && (
                <button onClick={() => removeEmail(idx)} className="text-red-400 text-xs hover:text-red-300">Remove</button>
              )}
            </div>

            <div className="space-y-3">
              <FormField label="Subject *">
                <input
                  value={email.email_subject}
                  onChange={e => updateEmail(idx, 'email_subject', e.target.value)}
                  className="w-full px-3 py-2 bg-royal-800/60 border border-royal-600 rounded-lg text-white text-sm"
                  placeholder="e.g., {{recipient_name}}, you've earned a special reward!"
                />
                <p className="text-royal-400 text-xs mt-1">Supports: {'{{recipient_name}}'}, {'{{site_name}}'}</p>
              </FormField>

              {form.schedule_type === 'series' && idx > 0 && (
                <FormField label="Delay (days after previous email)">
                  <input
                    type="number"
                    min="0"
                    value={email.delay_days}
                    onChange={e => updateEmail(idx, 'delay_days', parseInt(e.target.value) || 0)}
                    className="w-32 px-3 py-2 bg-royal-800/60 border border-royal-600 rounded-lg text-white text-sm"
                  />
                </FormField>
              )}

              <FormField label="Email HTML *">
                {/*
                  For informational campaigns: load from Body Templates (whole emails).
                  For reward campaigns: load from Reward Templates of the matching type
                  (also whole emails — no double-template confusion now).
                */}
                {!isReward && bodyTemplates.length > 0 && (
                  <div className="mb-2">
                    <select
                      value=""
                      onChange={e => {
                        const templateId = parseInt(e.target.value);
                        if (!templateId) return;
                        const tmpl = bodyTemplates.find(bt => bt.bodytemplateid === templateId);
                        if (!tmpl) return;
                        if (email.email_html_content && !window.confirm('Replace current email content with this template?')) return;
                        updateEmail(idx, 'email_html_content', tmpl.htmlcontent);
                      }}
                      className="w-full px-3 py-2 bg-royal-800/80 border border-gold/30 rounded-lg text-gold text-sm"
                    >
                      <option value="">Load from body template (whole email)...</option>
                      {bodyTemplates.filter(bt => bt.isactive).map(bt => (
                        <option key={bt.bodytemplateid} value={bt.bodytemplateid}>
                          {bt.name}{bt.description ? ` — ${bt.description.substring(0, 60)}` : ''}
                        </option>
                      ))}
                    </select>
                  </div>
                )}

                {isReward && filteredRewardTemplates.length > 0 && (
                  <div className="mb-2">
                    <select
                      value=""
                      onChange={e => {
                        const templateId = parseInt(e.target.value);
                        if (!templateId) return;
                        const tmpl = filteredRewardTemplates.find(rt => rt.rewardtemplateid === templateId);
                        if (!tmpl) return;
                        if (email.email_html_content && !window.confirm('Replace current email content with this template?')) return;
                        updateEmail(idx, 'email_html_content', tmpl.htmlcontent);
                      }}
                      className="w-full px-3 py-2 bg-royal-800/80 border border-gold/30 rounded-lg text-gold text-sm"
                    >
                      <option value="">Load from reward template (whole email)...</option>
                      {filteredRewardTemplates.filter(rt => rt.isactive).map(rt => (
                        <option key={rt.rewardtemplateid} value={rt.rewardtemplateid}>
                          {rt.name}{rt.isdefault ? ' (default)' : ''}
                        </option>
                      ))}
                    </select>
                  </div>
                )}

                <textarea
                  value={email.email_html_content}
                  onChange={e => updateEmail(idx, 'email_html_content', e.target.value)}
                  rows={12}
                  className="w-full px-3 py-2 bg-royal-800/60 border border-royal-600 rounded-lg text-white text-sm font-mono"
                  placeholder="Paste your complete email HTML here, or load a template above to start with..."
                />
                <p className="text-royal-400 text-xs mt-1">
                  This is the <strong className="text-gold">whole email</strong> (header markup, body content, unsubscribe link, footer all in one).
                  Supports: {'{{recipient_name}}'}, {'{{recipient_email}}'}, {'{{site_name}}'}, {'{{unsubscribe_url}}'}
                  {isReward && <>, plus reward placeholders specific to {form.campaign_type}.</>}
                </p>
              </FormField>

              {/* HTML Preview */}
              {email.email_html_content && (
                <div className="border border-royal-600 rounded-lg overflow-hidden">
                  <div className="bg-royal-800/60 px-3 py-1.5 border-b border-royal-600">
                    <p className="text-royal-400 text-xs">Preview</p>
                  </div>
                  <div
                    className="bg-white p-4 max-h-64 overflow-auto"
                    dangerouslySetInnerHTML={{ __html: email.email_html_content }}
                  />
                </div>
              )}
            </div>
          </div>
        ))}

        {form.schedule_type === 'series' && (
          <Button variant="outline" onClick={addEmail}>+ Add Email to Series</Button>
        )}
      </div>

      {/* Reward DATA configuration — only when campaign_type !== 'informational'.
          The reward TEMPLATE (whole-email HTML) lives inside the email field above;
          this block collects the DATA that fills its placeholders at send time. */}
      {isReward && (
        <div className="bg-royal-800/40 border border-gold/30 rounded-lg p-4 space-y-4">
          <div className="flex items-center gap-2">
            <h3 className="text-gold text-sm font-medium">🎁 Reward Data</h3>
            <span className="text-royal-400 text-xs">— values that fill the reward placeholders in your template</span>
          </div>

          {form.campaign_type === 'reward_text' && (
            <FormField label="Reward Text *">
              <textarea
                value={form.reward_config.reward_text || ''}
                onChange={e => updateRewardConfig('reward_text', e.target.value)}
                rows={3}
                className="w-full px-3 py-2 bg-royal-800/60 border border-royal-600 rounded-lg text-white text-sm"
                placeholder="e.g., Congratulations! As a top sender, you've earned..."
              />
              <p className="text-royal-400 text-xs mt-1">Substituted into {'{{reward_text}}'} in the template.</p>
            </FormField>
          )}

          {form.campaign_type === 'reward_access' && (
            <>
              <FormField label="Access Name *">
                <input
                  value={form.reward_config.access_name || ''}
                  onChange={e => updateRewardConfig('access_name', e.target.value)}
                  className="w-full px-3 py-2 bg-royal-800/60 border border-royal-600 rounded-lg text-white text-sm"
                  placeholder="e.g., First 1000 Sends Collection"
                />
                <div className="mt-1.5 p-2 bg-purple-900/20 border border-purple-500/20 rounded-lg">
                  <p className="text-purple-300 text-xs leading-relaxed">
                    <strong>This is what the user sees.</strong> Granted users will see their special access eCards grouped under this name
                    on the eCards page. Choose something descriptive and friendly — e.g., "Holiday 2026 Collection" or "Top Sender Rewards".
                  </p>
                </div>
              </FormField>

              <FormField label="Select eCard Templates *">
                <div className="max-h-48 overflow-auto border border-royal-600 rounded-lg p-2 space-y-1">
                  {templates.map(t => {
                    const selected = (form.reward_config.template_ids || []).includes(t.templateid);
                    return (
                      <label key={t.templateid} className="flex items-center gap-2 text-sm text-white cursor-pointer hover:bg-royal-800/40 px-2 py-1 rounded">
                        <input
                          type="checkbox"
                          checked={selected}
                          onChange={() => {
                            const current = form.reward_config.template_ids || [];
                            const next = selected
                              ? current.filter(id => id !== t.templateid)
                              : [...current, t.templateid];
                            updateRewardConfig('template_ids', next);
                          }}
                          className="rounded"
                        />
                        {t.name} <span className="text-royal-400 text-xs">({t.categoryname})</span>
                        {!t.isavailable && (
                          <span className="px-1.5 py-0.5 bg-yellow-900/40 text-yellow-400 text-xs rounded border border-yellow-600/30">
                            Unpublished
                          </span>
                        )}
                      </label>
                    );
                  })}
                </div>
                <p className="text-royal-400 text-xs mt-1">{(form.reward_config.template_ids || []).length} template(s) selected</p>
                <div className="mt-1.5 p-2 bg-royal-800/40 border border-royal-600/50 rounded-lg">
                  <p className="text-royal-300 text-xs leading-relaxed">
                    Unpublished templates are only visible to users with an active special access grant. They will not appear in the public eCards listing.
                    Published templates with special access grants are hidden from the public listing but remain accessible to granted users.
                  </p>
                </div>
              </FormField>

              <div className="grid grid-cols-2 gap-4">
                <FormField label="Access Starts">
                  <input
                    type="datetime-local"
                    value={form.reward_config.starts_at ? form.reward_config.starts_at.slice(0, 16) : ''}
                    onChange={e => updateRewardConfig('starts_at', e.target.value ? new Date(e.target.value).toISOString() : null)}
                    className="w-full px-3 py-2 bg-royal-800/60 border border-royal-600 rounded-lg text-white text-sm"
                  />
                  <p className="text-royal-400 text-xs mt-1">Leave blank for immediate</p>
                </FormField>

                <FormField label="Access Expires">
                  <input
                    type="datetime-local"
                    value={form.reward_config.expires_at ? form.reward_config.expires_at.slice(0, 16) : ''}
                    onChange={e => updateRewardConfig('expires_at', e.target.value ? new Date(e.target.value).toISOString() : null)}
                    className="w-full px-3 py-2 bg-royal-800/60 border border-royal-600 rounded-lg text-white text-sm"
                  />
                  <p className="text-royal-400 text-xs mt-1">Leave blank for never</p>
                </FormField>
              </div>
            </>
          )}

          {form.campaign_type === 'reward_giftcode' && (
            <>
              <FormField label="Gift Plan *">
                <select
                  value={form.gift_usertypeid}
                  onChange={e => updateForm('gift_usertypeid', parseInt(e.target.value) || '')}
                  className="w-full px-3 py-2 bg-royal-800/60 border border-royal-600 rounded-lg text-white text-sm"
                >
                  <option value="">Select a plan...</option>
                  {plans.map(p => (
                    <option key={p.usertypeid} value={p.usertypeid}>{p.typename} (${p.priceyearly}/yr)</option>
                  ))}
                </select>
                <p className="text-royal-400 text-xs mt-1">Substituted into {'{{plan_name}}'}; gift codes auto-generated per recipient.</p>
              </FormField>

              <div className="grid grid-cols-2 gap-4">
                <FormField label="Interval">
                  <select
                    value={form.gift_interval}
                    onChange={e => updateForm('gift_interval', e.target.value)}
                    className="w-full px-3 py-2 bg-royal-800/60 border border-royal-600 rounded-lg text-white text-sm"
                  >
                    <option value="yearly">Yearly (365 days)</option>
                    <option value="monthly">Monthly (30 days)</option>
                  </select>
                </FormField>

                <FormField label="Duration (days)">
                  <input
                    type="number"
                    value={form.gift_duration_days}
                    onChange={e => updateForm('gift_duration_days', parseInt(e.target.value) || 365)}
                    className="w-full px-3 py-2 bg-royal-800/60 border border-royal-600 rounded-lg text-white text-sm"
                  />
                </FormField>
              </div>
            </>
          )}
        </div>
      )}

      {!isReward && (
        <div className="bg-royal-800/40 border border-royal-600 rounded-lg p-4 text-center">
          <p className="text-royal-300 text-sm">
            This is an informational campaign — the email above will be sent as-is.
          </p>
        </div>
      )}
    </div>
  );
}


// ────────────────────────────────────────────────────────────
// STEP 4: REVIEW
// ────────────────────────────────────────────────────────────

function ReviewStep({ form, audienceFilters, emails, plans, validation }) {
  const manualUserCount = audienceFilters.manual?.userids?.length || 0;
  const manualLeadCount = audienceFilters.manual?.salesleadids?.length || 0;
  const groupCount = audienceFilters.groups?.length || 0;

  return (
    <div className="space-y-4">
      {!validation.valid && (
        <div className="bg-red-900/30 border border-red-700 rounded-lg p-3">
          <p className="text-red-300 text-sm font-medium mb-1">⚠ Required fields are missing:</p>
          <ul className="text-red-200 text-xs list-disc list-inside space-y-0.5">
            {Object.entries(validation.errors).flatMap(([step, errs]) =>
              errs.map((e, i) => <li key={`${step}-${i}`}>{e} <span className="text-red-400">({step})</span></li>)
            )}
          </ul>
        </div>
      )}

      <div className="bg-royal-800/40 border border-royal-600 rounded-lg p-4 space-y-2">
        <ReviewRow label="Name" value={form.name || '—'} />
        <ReviewRow label="Type" value={form.campaign_type} />
        <ReviewRow label="Audience Source" value={form.audience_source} />
        <ReviewRow label="Schedule" value={
          form.schedule_type === 'scheduled' ? `Send Later: ${form.scheduled_at || '(not set)'}` :
          form.schedule_type === 'triggered' ? 'Triggered (automated)' :
          form.schedule_type === 'series' ? 'Drip Series' :
          'Send Now'
        } />
        {form.schedule_type === 'triggered' && (
          <>
            <ReviewRow label="Start Watching" value={form.triggered_start_at ? new Date(form.triggered_start_at).toLocaleString() : 'Immediately (all matching)'} />
            <ReviewRow label="Stop Watching" value={form.triggered_end_at ? new Date(form.triggered_end_at).toLocaleString() : 'Never (until cancelled)'} />
          </>
        )}
        <ReviewRow label="Emails" value={`${emails.length} email(s)`} />
        <ReviewRow label="Audience" value={
          `${groupCount} condition group(s)` +
          (manualUserCount ? ` · ${manualUserCount} manual user(s)` : '') +
          (manualLeadCount ? ` · ${manualLeadCount} manual lead(s)` : '')
        } />
        {form.campaign_type === 'reward_giftcode' && (
          <ReviewRow label="Gift Plan" value={plans.find(p => p.usertypeid === form.gift_usertypeid)?.typename || 'Not selected'} />
        )}
        {form.campaign_type === 'reward_access' && (
          <ReviewRow label="Access Templates" value={`${(form.reward_config.template_ids || []).length} template(s)`} />
        )}
      </div>
    </div>
  );
}

function ReviewRow({ label, value }) {
  return (
    <div className="flex items-center justify-between text-sm">
      <span className="text-royal-400">{label}</span>
      <span className="text-white">{value}</span>
    </div>
  );
}


// ════════════════════════════════════════════════════════════
// AUDIENCE BUILDER COMPONENT (conditions only)
// ════════════════════════════════════════════════════════════

function AudienceBuilder({ filters, onChange, audienceSource }) {
  const [columnsData, setColumnsData] = useState(null);

  useEffect(() => {
    marketingService.getAudienceColumns().then(setColumnsData).catch(() => {});
  }, []);

  const addGroup = () => {
    onChange({
      ...filters,
      groups: [...(filters.groups || []), { logic: 'AND', conditions: [] }],
    });
  };

  const removeGroup = (gIdx) => {
    onChange({
      ...filters,
      groups: filters.groups.filter((_, i) => i !== gIdx),
    });
  };

  const addCondition = (gIdx) => {
    const newGroups = [...filters.groups];
    newGroups[gIdx] = {
      ...newGroups[gIdx],
      conditions: [...(newGroups[gIdx].conditions || []), { table: '', column: '', operator: '', value: '', unit: 'days' }],
    };
    onChange({ ...filters, groups: newGroups });
  };

  const removeCondition = (gIdx, cIdx) => {
    const newGroups = [...filters.groups];
    newGroups[gIdx] = {
      ...newGroups[gIdx],
      conditions: newGroups[gIdx].conditions.filter((_, i) => i !== cIdx),
    };
    onChange({ ...filters, groups: newGroups });
  };

  const updateCondition = (gIdx, cIdx, key, val) => {
    const newGroups = [...filters.groups];
    const cond = { ...newGroups[gIdx].conditions[cIdx], [key]: val };

    if (key === 'table') {
      cond.column = '';
      cond.operator = '';
      cond.value = '';
    }
    if (key === 'column') {
      cond.operator = '';
      cond.value = '';
    }

    newGroups[gIdx].conditions[cIdx] = cond;
    onChange({ ...filters, groups: newGroups });
  };

  const availableTables = [];
  if (audienceSource === 'users' || audienceSource === 'both') {
    availableTables.push(
      { value: 'users', label: 'Users' },
      { value: 'user_types', label: 'User Types (Plan)' },
      { value: 'moonbeams', label: 'Moonbeams (eCards)' },
      { value: 'moonbeam_recipients', label: 'Moonbeam Recipients' },
    );
  }
  if (audienceSource === 'sales_leads' || audienceSource === 'both') {
    availableTables.push(
      { value: 'sales_leads', label: 'Sales Leads' },
      { value: 'lead_industry_types', label: 'Lead Industries' },
      { value: 'sales_lead_sources', label: 'Lead Sources' },
    );
  }

  const getColumnsForTable = (tableName) => {
    if (!columnsData?.columns) return [];
    return columnsData.columns[tableName] || [];
  };

  const getOperators = (colType) => {
    switch (colType) {
      case 'boolean':
        return [
          { value: 'is_true', label: 'Is True' },
          { value: 'is_false', label: 'Is False' },
        ];
      case 'timestamp':
        return [
          { value: 'older_than', label: 'Older Than' },
          { value: 'newer_than', label: 'Newer Than' },
          { value: 'before', label: 'Before (date)' },
          { value: 'after', label: 'After (date)' },
          { value: 'between_ago', label: 'Between (relative)' },
          { value: 'is_null', label: 'Is Null' },
          { value: 'is_not_null', label: 'Is Not Null' },
        ];
      case 'varchar':
        return [
          { value: 'equals', label: 'Equals' },
          { value: 'contains', label: 'Contains' },
          { value: 'is_null', label: 'Is Null' },
          { value: 'is_not_null', label: 'Is Not Null' },
        ];
      case 'integer':
      case 'aggregate':
        return [
          { value: 'eq', label: '=' },
          { value: 'gt', label: '>' },
          { value: 'gte', label: '≥' },
          { value: 'lt', label: '<' },
          { value: 'lte', label: '≤' },
        ];
      default:
        return [];
    }
  };

  const needsValue = (op) => !['is_true', 'is_false', 'is_null', 'is_not_null'].includes(op);
  const needsUnit = (op) => ['older_than', 'newer_than', 'between_ago'].includes(op);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-royal-200 text-sm">
          Groups are combined with <strong className="text-gold">OR</strong>. Conditions within a group use <strong className="text-gold">AND</strong>.
        </p>
      </div>

      {(filters.groups || []).map((group, gIdx) => (
        <div key={gIdx} className="border border-royal-600 rounded-lg p-4 bg-royal-800/30">
          <div className="flex items-center justify-between mb-3">
            <span className="text-white text-sm font-medium">Group {gIdx + 1} — All conditions must match (AND)</span>
            {filters.groups.length > 1 && (
              <button onClick={() => removeGroup(gIdx)} className="text-red-400 text-xs hover:text-red-300">Remove Group</button>
            )}
          </div>

          <div className="space-y-2">
            {(group.conditions || []).map((cond, cIdx) => {
              const columns = getColumnsForTable(cond.table);
              const selectedCol = columns.find(c => c.column === cond.column);
              const operators = selectedCol ? getOperators(selectedCol.type) : [];

              return (
                <div key={cIdx} className="flex flex-wrap items-center gap-2">
                  <select
                    value={cond.table}
                    onChange={e => updateCondition(gIdx, cIdx, 'table', e.target.value)}
                    className="px-2 py-1.5 bg-royal-800/60 border border-royal-600 rounded text-xs text-white min-w-[130px]"
                  >
                    <option value="">Table...</option>
                    {availableTables.map(t => (
                      <option key={t.value} value={t.value}>{t.label}</option>
                    ))}
                  </select>

                  <select
                    value={cond.column}
                    onChange={e => updateCondition(gIdx, cIdx, 'column', e.target.value)}
                    className="px-2 py-1.5 bg-royal-800/60 border border-royal-600 rounded text-xs text-white min-w-[160px]"
                    disabled={!cond.table}
                  >
                    <option value="">Column...</option>
                    {columns.map(c => (
                      <option key={c.column} value={c.column}>{c.label}</option>
                    ))}
                  </select>

                  <select
                    value={cond.operator}
                    onChange={e => updateCondition(gIdx, cIdx, 'operator', e.target.value)}
                    className="px-2 py-1.5 bg-royal-800/60 border border-royal-600 rounded text-xs text-white min-w-[120px]"
                    disabled={!cond.column}
                  >
                    <option value="">Operator...</option>
                    {operators.map(o => (
                      <option key={o.value} value={o.value}>{o.label}</option>
                    ))}
                  </select>

                  {cond.operator && needsValue(cond.operator) && (
                    <>
                      {cond.operator === 'between_ago' ? (
                        <>
                          <input
                            type="number"
                            value={Array.isArray(cond.value) ? cond.value[0] : ''}
                            onChange={e => updateCondition(gIdx, cIdx, 'value', [parseInt(e.target.value) || 0, Array.isArray(cond.value) ? cond.value[1] : 0])}
                            className="w-16 px-2 py-1.5 bg-royal-800/60 border border-royal-600 rounded text-xs text-white"
                            placeholder="min"
                          />
                          <span className="text-royal-400 text-xs">to</span>
                          <input
                            type="number"
                            value={Array.isArray(cond.value) ? cond.value[1] : ''}
                            onChange={e => updateCondition(gIdx, cIdx, 'value', [Array.isArray(cond.value) ? cond.value[0] : 0, parseInt(e.target.value) || 0])}
                            className="w-16 px-2 py-1.5 bg-royal-800/60 border border-royal-600 rounded text-xs text-white"
                            placeholder="max"
                          />
                        </>
                      ) : selectedCol?.type === 'timestamp' && ['before', 'after'].includes(cond.operator) ? (
                        <input
                          type="date"
                          value={cond.value || ''}
                          onChange={e => updateCondition(gIdx, cIdx, 'value', e.target.value)}
                          className="px-2 py-1.5 bg-royal-800/60 border border-royal-600 rounded text-xs text-white"
                        />
                      ) : (
                        <input
                          type={['integer', 'aggregate'].includes(selectedCol?.type) || needsUnit(cond.operator) ? 'number' : 'text'}
                          value={cond.value ?? ''}
                          onChange={e => updateCondition(gIdx, cIdx, 'value', e.target.value)}
                          className="w-24 px-2 py-1.5 bg-royal-800/60 border border-royal-600 rounded text-xs text-white"
                          placeholder="Value..."
                        />
                      )}
                    </>
                  )}

                  {cond.operator && needsUnit(cond.operator) && (
                    <select
                      value={cond.unit || 'days'}
                      onChange={e => updateCondition(gIdx, cIdx, 'unit', e.target.value)}
                      className="px-2 py-1.5 bg-royal-800/60 border border-royal-600 rounded text-xs text-white"
                    >
                      <option value="days">Days</option>
                      <option value="weeks">Weeks</option>
                      <option value="months">Months</option>
                    </select>
                  )}

                  <button
                    onClick={() => removeCondition(gIdx, cIdx)}
                    className="text-red-400 text-xs hover:text-red-300 px-1"
                  >
                    ✕
                  </button>
                </div>
              );
            })}
          </div>

          <button
            onClick={() => addCondition(gIdx)}
            className="mt-3 text-gold text-xs hover:text-gold-light transition-colors"
          >
            + Add Condition
          </button>
        </div>
      ))}

      {(filters.groups || []).length > 0 && (
        <div className="text-center text-royal-400 text-xs uppercase tracking-wider">— OR —</div>
      )}

      <button
        onClick={addGroup}
        className="w-full py-2 border border-dashed border-royal-600 rounded-lg text-royal-300 text-sm hover:border-gold/50 hover:text-gold transition-colors"
      >
        + Add Group (OR)
      </button>
    </div>
  );
}


// ════════════════════════════════════════════════════════════
// RECIPIENTS MODAL
// ════════════════════════════════════════════════════════════

function RecipientsModal({ campaignId, onClose }) {
  const [recipients, setRecipients] = useState([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [statusFilter, setStatusFilter] = useState('');

  useEffect(() => {
    marketingService.getCampaignRecipients(campaignId, { page, status: statusFilter || undefined, limit: 25 })
      .then(d => {
        setRecipients(d.recipients || []);
        setTotalPages(d.totalPages || 1);
        setTotal(d.total || 0);
      })
      .catch(() => {});
  }, [campaignId, page, statusFilter]);

  const statusColors = {
    pending: 'text-royal-300',
    sent: 'text-green-400',
    failed: 'text-red-400',
    skipped: 'text-yellow-400',
  };

  return (
    <Modal isOpen={true} onClose={onClose} title={`Campaign Recipients (${total})`} wide>
      <div className="mb-3">
        <select
          value={statusFilter}
          onChange={e => { setStatusFilter(e.target.value); setPage(1); }}
          className="px-3 py-1.5 bg-royal-800/60 border border-royal-600 rounded text-sm text-white"
        >
          <option value="">All</option>
          <option value="pending">Pending</option>
          <option value="sent">Sent</option>
          <option value="failed">Failed</option>
          <option value="skipped">Skipped</option>
        </select>
      </div>

      <div className="max-h-96 overflow-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-royal-600 text-royal-400 text-xs">
              <th className="text-left py-2 px-2">Type</th>
              <th className="text-left py-2 px-2">Name</th>
              <th className="text-left py-2 px-2">Email</th>
              <th className="text-left py-2 px-2">Status</th>
              <th className="text-left py-2 px-2">Gift Code</th>
              <th className="text-left py-2 px-2">Sent At</th>
            </tr>
          </thead>
          <tbody>
            {recipients.map(r => (
              <tr key={r.recipientid} className="border-b border-royal-700/50">
                <td className="py-2 px-2 text-royal-300">{r.recipient_type === 'user' ? '👤' : '📋'}</td>
                <td className="py-2 px-2 text-white">{r.recipient_name || '—'}</td>
                <td className="py-2 px-2 text-royal-200">{r.email}</td>
                <td className={`py-2 px-2 ${statusColors[r.status] || ''}`}>
                  {r.status}{r.skip_reason ? ` (${r.skip_reason})` : ''}
                </td>
                <td className="py-2 px-2 text-royal-300 font-mono text-xs">{r.gift_code || '—'}</td>
                <td className="py-2 px-2 text-royal-400 text-xs">{r.sent_at ? new Date(r.sent_at).toLocaleString() : '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <SimplePagination page={page} totalPages={totalPages} onPageChange={setPage} />
    </Modal>
  );
}


// ════════════════════════════════════════════════════════════
// SALES LEADS TAB
// ════════════════════════════════════════════════════════════

function LeadsTab() {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [editing, setEditing] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [sources, setSources] = useState([]);
  const [industries, setIndustries] = useState([]);

  const loadLeads = useCallback(() => {
    setLoading(true);
    marketingService.getLeads({ page, search: search || undefined })
      .then(d => { setLeads(d.leads || []); setTotalPages(d.totalPages || 1); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [page, search]);

  useEffect(() => { loadLeads(); }, [loadLeads]);
  useEffect(() => {
    marketingService.getLeadSources().then(d => setSources(d.sources || [])).catch(() => {});
    marketingService.getLeadIndustries().then(d => setIndustries(d.industries || [])).catch(() => {});
  }, []);

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this lead?')) return;
    try {
      await marketingService.deleteLead(id);
      loadLeads();
    } catch (err) { alert(err.data?.error || 'Delete failed'); }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <input
          value={search}
          onChange={e => { setSearch(e.target.value); setPage(1); }}
          placeholder="Search leads..."
          className="px-3 py-2 bg-royal-800/60 border border-royal-600 rounded-lg text-sm text-white w-64"
        />
        <Button onClick={() => { setEditing(null); setShowForm(true); }}>+ Add Lead</Button>
      </div>

      {loading ? <LoadingSpinner /> : leads.length === 0 ? (
        <EmptyState message="No sales leads yet." />
      ) : (
        <div className="overflow-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-royal-600 text-royal-400 text-xs">
                <th className="text-left py-2 px-2">Name</th>
                <th className="text-left py-2 px-2">Email</th>
                <th className="text-left py-2 px-2">Business</th>
                <th className="text-left py-2 px-2">Source</th>
                <th className="text-left py-2 px-2">Industry</th>
                <th className="text-left py-2 px-2">Unsub</th>
                <th className="text-right py-2 px-2">Actions</th>
              </tr>
            </thead>
            <tbody>
              {leads.map(l => (
                <tr key={l.salesleadid} className="border-b border-royal-700/50">
                  <td className="py-2 px-2 text-white">{[l.firstname, l.lastname].filter(Boolean).join(' ') || '—'}</td>
                  <td className="py-2 px-2 text-royal-200">{l.email}</td>
                  <td className="py-2 px-2 text-royal-300">{l.businessname || '—'}</td>
                  <td className="py-2 px-2 text-royal-300">{l.sourcename || '—'}</td>
                  <td className="py-2 px-2 text-royal-300">{l.industryname || '—'}</td>
                  <td className="py-2 px-2">{l.unsubscribed ? <span className="text-red-400">Yes</span> : <span className="text-green-400">No</span>}</td>
                  <td className="py-2 px-2 text-right">
                    <SmallBtn onClick={() => { setEditing(l); setShowForm(true); }} label="Edit" />
                    <SmallBtn onClick={() => handleDelete(l.salesleadid)} label="Del" variant="red" />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <SimplePagination page={page} totalPages={totalPages} onPageChange={setPage} />

      {showForm && (
        <LeadFormModal
          lead={editing}
          sources={sources}
          industries={industries}
          onClose={() => { setShowForm(false); setEditing(null); }}
          onSaved={() => { setShowForm(false); setEditing(null); loadLeads(); }}
        />
      )}
    </div>
  );
}

function LeadFormModal({ lead, sources, industries, onClose, onSaved }) {
  const [form, setForm] = useState({
    firstname: lead?.firstname || '',
    lastname: lead?.lastname || '',
    email: lead?.email || '',
    website: lead?.website || '',
    businessname: lead?.businessname || '',
    leadindustrytypeid: lead?.leadindustrytypeid || '',
    sourceid: lead?.sourceid || '',
    unsubscribed: lead?.unsubscribed || false,
  });
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    if (!form.email) return alert('Email is required');
    setSaving(true);
    try {
      if (lead) {
        await marketingService.updateLead(lead.salesleadid, form);
      } else {
        await marketingService.createLead(form);
      }
      onSaved();
    } catch (err) { alert(err.data?.error || 'Save failed'); }
    setSaving(false);
  };

  const upd = (k, v) => setForm(p => ({ ...p, [k]: v }));

  return (
    <Modal isOpen={true} onClose={onClose} title={lead ? 'Edit Lead' : 'New Lead'}>
      <div className="space-y-3">
        <div className="grid grid-cols-2 gap-3">
          <FormField label="First Name">
            <input value={form.firstname} onChange={e => upd('firstname', e.target.value)} className="w-full px-3 py-2 bg-royal-800/60 border border-royal-600 rounded-lg text-white text-sm" />
          </FormField>
          <FormField label="Last Name">
            <input value={form.lastname} onChange={e => upd('lastname', e.target.value)} className="w-full px-3 py-2 bg-royal-800/60 border border-royal-600 rounded-lg text-white text-sm" />
          </FormField>
        </div>
        <FormField label="Email *">
          <input value={form.email} onChange={e => upd('email', e.target.value)} className="w-full px-3 py-2 bg-royal-800/60 border border-royal-600 rounded-lg text-white text-sm" />
        </FormField>
        <FormField label="Business Name">
          <input value={form.businessname} onChange={e => upd('businessname', e.target.value)} className="w-full px-3 py-2 bg-royal-800/60 border border-royal-600 rounded-lg text-white text-sm" />
        </FormField>
        <FormField label="Website">
          <input value={form.website} onChange={e => upd('website', e.target.value)} className="w-full px-3 py-2 bg-royal-800/60 border border-royal-600 rounded-lg text-white text-sm" />
        </FormField>
        <div className="grid grid-cols-2 gap-3">
          <FormField label="Source">
            <select value={form.sourceid} onChange={e => upd('sourceid', e.target.value || null)} className="w-full px-3 py-2 bg-royal-800/60 border border-royal-600 rounded-lg text-white text-sm">
              <option value="">None</option>
              {sources.map(s => <option key={s.sourceid} value={s.sourceid}>{s.name}</option>)}
            </select>
          </FormField>
          <FormField label="Industry">
            <select value={form.leadindustrytypeid} onChange={e => upd('leadindustrytypeid', e.target.value || null)} className="w-full px-3 py-2 bg-royal-800/60 border border-royal-600 rounded-lg text-white text-sm">
              <option value="">None</option>
              {industries.map(i => <option key={i.leadindustrytypeid} value={i.leadindustrytypeid}>{i.industryname}</option>)}
            </select>
          </FormField>
        </div>
      </div>
      <div className="flex justify-end gap-3 mt-5">
        <Button variant="ghost" onClick={onClose}>Cancel</Button>
        <Button onClick={handleSave} disabled={saving}>{saving ? 'Saving...' : 'Save'}</Button>
      </div>
    </Modal>
  );
}


// ════════════════════════════════════════════════════════════
// LEAD SOURCES TAB
// ════════════════════════════════════════════════════════════

function LeadSourcesTab() {
  return <SimpleCrudTab service={marketingService} getMethod="getLeadSources" dataKey="sources" idKey="sourceid" nameKey="name" label="Lead Source" createMethod="createLeadSource" updateMethod="updateLeadSource" deleteMethod="deleteLeadSource" />;
}


// ════════════════════════════════════════════════════════════
// LEAD INDUSTRIES TAB
// ════════════════════════════════════════════════════════════

function LeadIndustriesTab() {
  return <SimpleCrudTab service={marketingService} getMethod="getLeadIndustries" dataKey="industries" idKey="leadindustrytypeid" nameKey="industryname" label="Lead Industry" createMethod="createLeadIndustry" updateMethod="updateLeadIndustry" deleteMethod="deleteLeadIndustry" extraFields={['displayorder', 'isactive']} />;
}


// ════════════════════════════════════════════════════════════
// EMAIL TEMPLATES TAB (Body Templates + Reward Templates)
// ════════════════════════════════════════════════════════════
//
// Reduced from 4 sub-tabs (headers/footers/bodies/rewards) to 2.
// Each template is now a WHOLE email — header markup, body content,
// unsubscribe link, and footer all baked into one HTML blob.

function EmailPartsTab() {
  const [subTab, setSubTab] = useState('bodies');

  return (
    <div>
      <div className="flex gap-2 mb-4">
        {['bodies', 'rewards'].map(t => (
          <button
            key={t}
            onClick={() => setSubTab(t)}
            className={`px-3 py-1.5 rounded text-xs font-medium transition-colors ${
              subTab === t ? 'bg-gold/20 text-gold border border-gold/30' : 'text-royal-300 border border-royal-600 hover:text-gold'
            }`}
          >
            {t === 'bodies' ? 'Body Templates (Informational)' : 'Reward Templates'}
          </button>
        ))}
      </div>

      <div className="mb-4 p-3 bg-royal-800/30 border border-royal-600/50 rounded-lg">
        <p className="text-royal-300 text-xs leading-relaxed">
          Each template here is a <strong className="text-gold">whole email</strong> — header, body, unsubscribe link, and footer all together in one HTML blob.
          Use <strong className="text-gold">Body Templates</strong> for informational campaigns (no reward) and <strong className="text-gold">Reward Templates</strong> for the three reward types.
          Templates can be loaded into a campaign's email field as a starting point and edited per-campaign.
        </p>
      </div>

      {subTab === 'bodies' && <BodyTemplatesCrud service={marketingService} />}
      {subTab === 'rewards' && <RewardTemplatesCrud service={marketingService} />}
    </div>
  );
}

function RewardTemplatesCrud({ service }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [showForm, setShowForm] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    service.getRewardTemplates().then(d => setItems(d.rewardTemplates || [])).catch(() => {}).finally(() => setLoading(false));
  }, [service]);

  useEffect(() => { load(); }, [load]);

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this reward template?')) return;
    try { await service.deleteRewardTemplate(id); load(); } catch (err) { alert(err.data?.error || 'Delete failed'); }
  };

  const typeColors = { text: 'text-blue-400', access: 'text-purple-400', giftcode: 'text-gold' };

  return (
    <div>
      <div className="flex justify-end mb-3">
        <Button onClick={() => { setEditing(null); setShowForm(true); }}>+ Add Reward Template</Button>
      </div>

      {loading ? <LoadingSpinner /> : items.length === 0 ? (
        <EmptyState message="No reward templates yet. Each reward template is a whole email — pick a reward type, write the full HTML email with reward placeholders inline." />
      ) : (
        <div className="space-y-3">
          {items.map(item => (
            <div key={item.rewardtemplateid} className="bg-royal-800/40 border border-royal-600 rounded-lg p-4">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <span className="text-white font-medium text-sm">{item.name}</span>
                  <span className={`text-xs ${typeColors[item.reward_type] || ''}`}>{item.reward_type}</span>
                  {item.isdefault && <span className="text-xs bg-gold/20 text-gold px-2 py-0.5 rounded">Default</span>}
                </div>
                <div className="flex gap-2">
                  <SmallBtn onClick={() => { setEditing(item); setShowForm(true); }} label="Edit" />
                  <SmallBtn onClick={() => handleDelete(item.rewardtemplateid)} label="Del" variant="red" />
                </div>
              </div>
              <div className="bg-white rounded p-2 max-h-32 overflow-auto" dangerouslySetInnerHTML={{ __html: item.htmlcontent }} />
            </div>
          ))}
        </div>
      )}

      {showForm && (
        <RewardTemplateFormModal
          item={editing}
          onClose={() => { setShowForm(false); setEditing(null); }}
          onSaved={async (data) => {
            try {
              if (editing) { await service.updateRewardTemplate(editing.rewardtemplateid, data); }
              else { await service.createRewardTemplate(data); }
              setShowForm(false); setEditing(null); load();
            } catch (err) { alert(err.data?.error || 'Save failed'); }
          }}
        />
      )}
    </div>
  );
}

function HtmlPartFormModal({ item, label, onClose, onSaved }) {
  const [form, setForm] = useState({
    name: item?.name || '',
    description: item?.description || '',
    htmlcontent: item?.htmlcontent || '',
    isdefault: item?.isdefault || false,
    isactive: item?.isactive !== false,
  });
  const [saving, setSaving] = useState(false);

  return (
    <Modal isOpen={true} onClose={onClose} title={item ? `Edit ${label}` : `New ${label}`}>
      <div className="space-y-3">
        <FormField label="Name">
          <input value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))} className="w-full px-3 py-2 bg-royal-800/60 border border-royal-600 rounded-lg text-white text-sm" />
        </FormField>
        <FormField label="Description">
          <input value={form.description} onChange={e => setForm(p => ({ ...p, description: e.target.value }))} placeholder="Short note about when to use this template" className="w-full px-3 py-2 bg-royal-800/60 border border-royal-600 rounded-lg text-white text-sm" />
        </FormField>
        <FormField label="HTML Content (whole email)">
          <textarea value={form.htmlcontent} onChange={e => setForm(p => ({ ...p, htmlcontent: e.target.value }))} rows={14} className="w-full px-3 py-2 bg-royal-800/60 border border-royal-600 rounded-lg text-white text-sm font-mono" />
          <p className="text-royal-400 text-xs mt-1">
            Include the entire email: header markup, body content, unsubscribe link, footer.
            Supports {'{{recipient_name}}'}, {'{{recipient_email}}'}, {'{{site_name}}'}, {'{{unsubscribe_url}}'}.
          </p>
        </FormField>
        {form.htmlcontent && (
          <div className="border border-royal-600 rounded-lg overflow-hidden">
            <div className="bg-royal-800/60 px-3 py-1 border-b border-royal-600"><p className="text-royal-400 text-xs">Preview</p></div>
            <div className="bg-white p-4 max-h-48 overflow-auto" dangerouslySetInnerHTML={{ __html: form.htmlcontent }} />
          </div>
        )}
        <div className="flex gap-4">
          <label className="flex items-center gap-2 text-sm text-white cursor-pointer">
            <input type="checkbox" checked={form.isactive} onChange={e => setForm(p => ({ ...p, isactive: e.target.checked }))} /> Active
          </label>
        </div>
      </div>
      <div className="flex justify-end gap-3 mt-5">
        <Button variant="ghost" onClick={onClose}>Cancel</Button>
        <Button onClick={async () => { setSaving(true); await onSaved(form); setSaving(false); }} disabled={saving}>
          {saving ? 'Saving...' : 'Save'}
        </Button>
      </div>
    </Modal>
  );
}

function RewardTemplateFormModal({ item, onClose, onSaved }) {
  const [form, setForm] = useState({
    name: item?.name || '',
    reward_type: item?.reward_type || 'text',
    htmlcontent: item?.htmlcontent || '',
    isdefault: item?.isdefault || false,
    isactive: item?.isactive !== false,
  });
  const [saving, setSaving] = useState(false);

  const placeholderHelp = {
    text: '{{reward_text}}',
    access: '{{access_name}}, {{access_card_count}}, {{access_start_date}}, {{access_end_date}}, {{access_card_list}}',
    giftcode: '{{gift_code}}, {{redeem_url}}, {{plan_name}}, {{duration_label}}, {{code_expires_date}}',
  };

  return (
    <Modal isOpen={true} onClose={onClose} title={item ? 'Edit Reward Template' : 'New Reward Template'}>
      <div className="space-y-3">
        <FormField label="Name">
          <input value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))} className="w-full px-3 py-2 bg-royal-800/60 border border-royal-600 rounded-lg text-white text-sm" />
        </FormField>
        <FormField label="Reward Type">
          <select value={form.reward_type} onChange={e => setForm(p => ({ ...p, reward_type: e.target.value }))} className="w-full px-3 py-2 bg-royal-800/60 border border-royal-600 rounded-lg text-white text-sm" disabled={!!item}>
            <option value="text">Text</option>
            <option value="access">Special Access</option>
            <option value="giftcode">Gift Code</option>
          </select>
        </FormField>
        <FormField label="HTML Content (whole email)">
          <textarea value={form.htmlcontent} onChange={e => setForm(p => ({ ...p, htmlcontent: e.target.value }))} rows={14} className="w-full px-3 py-2 bg-royal-800/60 border border-royal-600 rounded-lg text-white text-sm font-mono" />
          <p className="text-royal-400 text-xs mt-1">
            Include the entire email — header, body, reward content, unsubscribe link, footer.
            Standard placeholders: {'{{recipient_name}}'}, {'{{recipient_email}}'}, {'{{site_name}}'}, {'{{unsubscribe_url}}'}.<br />
            Reward placeholders for this type: <span className="text-gold">{placeholderHelp[form.reward_type]}</span>
          </p>
        </FormField>
        {form.htmlcontent && (
          <div className="border border-royal-600 rounded-lg overflow-hidden">
            <div className="bg-royal-800/60 px-3 py-1 border-b border-royal-600"><p className="text-royal-400 text-xs">Preview</p></div>
            <div className="bg-white p-4 max-h-48 overflow-auto" dangerouslySetInnerHTML={{ __html: form.htmlcontent }} />
          </div>
        )}
        <div className="flex gap-4">
          <label className="flex items-center gap-2 text-sm text-white cursor-pointer">
            <input type="checkbox" checked={form.isdefault} onChange={e => setForm(p => ({ ...p, isdefault: e.target.checked }))} /> Default for this type
          </label>
          <label className="flex items-center gap-2 text-sm text-white cursor-pointer">
            <input type="checkbox" checked={form.isactive} onChange={e => setForm(p => ({ ...p, isactive: e.target.checked }))} /> Active
          </label>
        </div>
      </div>
      <div className="flex justify-end gap-3 mt-5">
        <Button variant="ghost" onClick={onClose}>Cancel</Button>
        <Button onClick={async () => { setSaving(true); await onSaved(form); setSaving(false); }} disabled={saving}>
          {saving ? 'Saving...' : 'Save'}
        </Button>
      </div>
    </Modal>
  );
}


// ════════════════════════════════════════════════════════════
// BODY TEMPLATES CRUD
// ════════════════════════════════════════════════════════════

function BodyTemplatesCrud({ service }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [showForm, setShowForm] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    service.getBodyTemplates().then(d => setItems(d.bodyTemplates || [])).catch(() => {}).finally(() => setLoading(false));
  }, [service]);

  useEffect(() => { load(); }, [load]);

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this body template?')) return;
    try { await service.deleteBodyTemplate(id); load(); } catch (err) { alert(err.data?.error || 'Delete failed'); }
  };

  return (
    <div>
      <div className="flex justify-end mb-3">
        <Button onClick={() => { setEditing(null); setShowForm(true); }}>+ Add Body Template</Button>
      </div>

      {loading ? <LoadingSpinner /> : items.length === 0 ? (
        <EmptyState message="No body templates yet. Each body template is a whole email used for informational campaigns (no reward block)." />
      ) : (
        <div className="space-y-3">
          {items.map(item => (
            <div key={item.bodytemplateid} className="bg-royal-800/40 border border-royal-600 rounded-lg p-4">
              <div className="flex items-center justify-between mb-2">
                <div className="flex-1">
                  <span className="text-white font-medium text-sm">{item.name}</span>
                  {item.description && <p className="text-royal-400 text-xs mt-0.5">{item.description}</p>}
                  {!item.isactive && <span className="text-xs bg-red-900/30 text-red-400 px-2 py-0.5 rounded ml-2">Inactive</span>}
                </div>
                <div className="flex gap-2">
                  <SmallBtn onClick={() => { setEditing(item); setShowForm(true); }} label="Edit" />
                  <SmallBtn onClick={() => handleDelete(item.bodytemplateid)} label="Del" variant="red" />
                </div>
              </div>
              <div className="bg-white rounded p-2 max-h-32 overflow-auto" dangerouslySetInnerHTML={{ __html: item.htmlcontent }} />
            </div>
          ))}
        </div>
      )}

      {showForm && (
        <HtmlPartFormModal
          item={editing}
          label="Body Template"
          onClose={() => { setShowForm(false); setEditing(null); }}
          onSaved={async (data) => {
            try {
              const payload = { name: data.name, description: data.description || null, htmlcontent: data.htmlcontent, isactive: data.isactive };
              if (editing) { await service.updateBodyTemplate(editing.bodytemplateid, payload); }
              else { await service.createBodyTemplate(payload); }
              setShowForm(false); setEditing(null); load();
            } catch (err) { alert(err.data?.error || 'Save failed'); }
          }}
        />
      )}
    </div>
  );
}


// ════════════════════════════════════════════════════════════
// CLICK ANALYTICS MODAL
// ════════════════════════════════════════════════════════════

function ClickAnalyticsModal({ campaignId, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    marketingService.getCampaignClicks(campaignId)
      .then(setData)
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [campaignId]);

  return (
    <Modal isOpen={true} onClose={onClose} title="Click Analytics" wide>
      {loading ? (
        <LoadingSpinner />
      ) : !data ? (
        <EmptyState message="Unable to load click data." />
      ) : (
        <div className="space-y-6">
          <div className="grid grid-cols-4 gap-3">
            <div className="bg-royal-800/60 border border-royal-600 rounded-lg p-3 text-center">
              <p className="text-xl font-bold text-gold">{data.totalClicks}</p>
              <p className="text-royal-400 text-xs">Total Clicks</p>
            </div>
            <div className="bg-royal-800/60 border border-royal-600 rounded-lg p-3 text-center">
              <p className="text-xl font-bold text-blue-400">{data.uniqueClickers}</p>
              <p className="text-royal-400 text-xs">Unique Clickers</p>
            </div>
            <div className="bg-royal-800/60 border border-royal-600 rounded-lg p-3 text-center">
              <p className="text-xl font-bold text-green-400">{data.clickThroughRate}%</p>
              <p className="text-royal-400 text-xs">Click-Through Rate</p>
            </div>
            <div className="bg-royal-800/60 border border-royal-600 rounded-lg p-3 text-center">
              <p className="text-xl font-bold text-white">{data.totalSent}</p>
              <p className="text-royal-400 text-xs">Total Sent</p>
            </div>
          </div>

          {data.topLinks?.length > 0 && (
            <div>
              <h4 className="text-white text-sm font-medium mb-2">Top Links</h4>
              <div className="overflow-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-royal-600 text-royal-400 text-xs">
                      <th className="text-left py-2 px-2">Link</th>
                      <th className="text-left py-2 px-2">Label</th>
                      <th className="text-right py-2 px-2">Clicks</th>
                      <th className="text-right py-2 px-2">Unique</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.topLinks.map((link, i) => (
                      <tr key={i} className="border-b border-royal-700/50">
                        <td className="py-2 px-2 text-royal-200 text-xs max-w-[200px] truncate">
                          <a href={link.original_url} target="_blank" rel="noopener noreferrer" className="hover:text-gold transition-colors">
                            {link.original_url}
                          </a>
                        </td>
                        <td className="py-2 px-2 text-white text-xs">{link.link_label || '—'}</td>
                        <td className="py-2 px-2 text-gold text-right">{link.clicks}</td>
                        <td className="py-2 px-2 text-blue-400 text-right">{link.unique_clickers}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {data.recentClicks?.length > 0 && (
            <div>
              <h4 className="text-white text-sm font-medium mb-2">Recent Clicks</h4>
              <div className="max-h-48 overflow-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-royal-600 text-royal-400 text-xs">
                      <th className="text-left py-1.5 px-2">Recipient</th>
                      <th className="text-left py-1.5 px-2">Link</th>
                      <th className="text-left py-1.5 px-2">When</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.recentClicks.map((click, i) => (
                      <tr key={i} className="border-b border-royal-700/50">
                        <td className="py-1.5 px-2 text-white text-xs">{click.recipient_name || click.recipient_email || '—'}</td>
                        <td className="py-1.5 px-2 text-royal-300 text-xs truncate max-w-[180px]">{click.link_label || click.original_url}</td>
                        <td className="py-1.5 px-2 text-royal-400 text-xs">{new Date(click.clicked_at).toLocaleString()}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {data.totalClicks === 0 && (
            <EmptyState message="No clicks recorded yet. Links in emails are automatically tracked — data will appear here once recipients start clicking." />
          )}
        </div>
      )}

      <div className="flex justify-end mt-4">
        <Button variant="outline" onClick={onClose}>Close</Button>
      </div>
    </Modal>
  );
}


// ════════════════════════════════════════════════════════════
// SPECIAL ACCESS TAB
// ════════════════════════════════════════════════════════════

function SpecialAccessTab() {
  const [grants, setGrants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  const load = useCallback(() => {
    setLoading(true);
    marketingService.getSpecialAccess({ page })
      .then(d => { setGrants(d.accessGrants || []); setTotalPages(d.totalPages || 1); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [page]);

  useEffect(() => { load(); }, [load]);

  const handleDelete = async (id) => {
    if (!window.confirm('Revoke this access grant?')) return;
    try { await marketingService.deleteSpecialAccess(id); load(); } catch (err) { alert(err.data?.error || 'Delete failed'); }
  };

  return (
    <div>
      {loading ? <LoadingSpinner /> : grants.length === 0 ? (
        <EmptyState message="No special access grants yet. Execute a campaign with an access reward to create them." />
      ) : (
        <div className="overflow-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-royal-600 text-royal-400 text-xs">
                <th className="text-left py-2 px-2">User</th>
                <th className="text-left py-2 px-2">Template</th>
                <th className="text-left py-2 px-2">Access Name</th>
                <th className="text-left py-2 px-2">Campaign</th>
                <th className="text-left py-2 px-2">Starts</th>
                <th className="text-left py-2 px-2">Expires</th>
                <th className="text-right py-2 px-2">Actions</th>
              </tr>
            </thead>
            <tbody>
              {grants.map(g => (
                <tr key={g.accessid} className="border-b border-royal-700/50">
                  <td className="py-2 px-2 text-white">{[g.firstname, g.lastname].filter(Boolean).join(' ') || g.useremail}</td>
                  <td className="py-2 px-2 text-royal-200">{g.templatename}</td>
                  <td className="py-2 px-2 text-royal-300">{g.accessname || '—'}</td>
                  <td className="py-2 px-2 text-royal-300">{g.campaignname || 'Manual'}</td>
                  <td className="py-2 px-2 text-royal-400 text-xs">{g.startsat ? new Date(g.startsat).toLocaleDateString() : 'Immediate'}</td>
                  <td className="py-2 px-2 text-royal-400 text-xs">{g.expiresat ? new Date(g.expiresat).toLocaleDateString() : 'Never'}</td>
                  <td className="py-2 px-2 text-right">
                    <SmallBtn onClick={() => handleDelete(g.accessid)} label="Revoke" variant="red" />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <SimplePagination page={page} totalPages={totalPages} onPageChange={setPage} />
    </div>
  );
}


// ════════════════════════════════════════════════════════════
// GENERIC SIMPLE CRUD TAB
// ════════════════════════════════════════════════════════════

function SimpleCrudTab({ service, getMethod, dataKey, idKey, nameKey, label, createMethod, updateMethod, deleteMethod }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newName, setNewName] = useState('');
  const [editId, setEditId] = useState(null);
  const [editName, setEditName] = useState('');

  const load = useCallback(() => {
    setLoading(true);
    service[getMethod]().then(d => setItems(d[dataKey] || [])).catch(() => {}).finally(() => setLoading(false));
  }, [service, getMethod, dataKey]);

  useEffect(() => { load(); }, [load]);

  const handleCreate = async () => {
    if (!newName.trim()) return;
    try {
      const payload = nameKey === 'industryname' ? { industryname: newName } : { name: newName };
      await service[createMethod](payload);
      setNewName('');
      load();
    } catch (err) { alert(err.data?.error || 'Create failed'); }
  };

  const handleUpdate = async (id) => {
    if (!editName.trim()) return;
    try {
      const payload = nameKey === 'industryname' ? { industryname: editName } : { name: editName };
      await service[updateMethod](id, payload);
      setEditId(null);
      load();
    } catch (err) { alert(err.data?.error || 'Update failed'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm(`Delete this ${label.toLowerCase()}?`)) return;
    try { await service[deleteMethod](id); load(); } catch (err) { alert(err.data?.error || 'Delete failed'); }
  };

  return (
    <div>
      <div className="flex items-center gap-3 mb-4">
        <input
          value={newName}
          onChange={e => setNewName(e.target.value)}
          placeholder={`New ${label} name...`}
          className="flex-1 px-3 py-2 bg-royal-800/60 border border-royal-600 rounded-lg text-sm text-white"
          onKeyDown={e => e.key === 'Enter' && handleCreate()}
        />
        <Button onClick={handleCreate}>Add</Button>
      </div>

      {loading ? <LoadingSpinner /> : items.length === 0 ? (
        <EmptyState message={`No ${label.toLowerCase()}s yet.`} />
      ) : (
        <div className="space-y-2">
          {items.map(item => (
            <div key={item[idKey]} className="flex items-center justify-between bg-royal-800/40 border border-royal-600 rounded-lg px-4 py-3">
              {editId === item[idKey] ? (
                <div className="flex items-center gap-2 flex-1">
                  <input
                    value={editName}
                    onChange={e => setEditName(e.target.value)}
                    className="flex-1 px-3 py-1.5 bg-royal-800/60 border border-royal-600 rounded text-sm text-white"
                    onKeyDown={e => e.key === 'Enter' && handleUpdate(item[idKey])}
                    autoFocus
                  />
                  <SmallBtn onClick={() => handleUpdate(item[idKey])} label="Save" variant="green" />
                  <SmallBtn onClick={() => setEditId(null)} label="Cancel" />
                </div>
              ) : (
                <>
                  <span className="text-white text-sm">{item[nameKey]}</span>
                  <div className="flex gap-2">
                    <SmallBtn onClick={() => { setEditId(item[idKey]); setEditName(item[nameKey]); }} label="Edit" />
                    <SmallBtn onClick={() => handleDelete(item[idKey])} label="Del" variant="red" />
                  </div>
                </>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}


// ════════════════════════════════════════════════════════════
// HELP MODAL
// ════════════════════════════════════════════════════════════

function HelpModal({ isOpen, onClose }) {
  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Marketing Campaigns — Help & Notes" wide>
      <div className="max-h-[70vh] overflow-auto space-y-6 text-sm text-royal-200">

        <section>
          <h3 className="text-gold font-medium mb-2">Audience Filtering — How It Works</h3>
          <p className="mb-2">
            The audience builder creates SQL conditions behind the scenes. You select a <strong className="text-white">table</strong>, a <strong className="text-white">column</strong>,
            an <strong className="text-white">operator</strong>, and a <strong className="text-white">value</strong>. Conditions within a group are combined with <strong className="text-gold">AND</strong> (all must be true).
            Groups are combined with <strong className="text-gold">OR</strong> (any group can match).
          </p>
          <p className="mb-2">
            Example: "Users on a free plan who signed up more than 3 months ago OR users who have delivered to more than 1000 recipients" would be two groups.
          </p>
        </section>

        <section>
          <h3 className="text-gold font-medium mb-2">Manual Selection</h3>
          <p className="mb-2">
            In addition to filter conditions, you can hand-pick specific users or sales leads via the
            <strong className="text-white"> Manual Selection</strong> panel. The two are <strong className="text-white">merged</strong>:
            anyone matched by conditions <em>or</em> manually selected ends up in the recipient list (deduped, so picking
            someone who also matches a condition only sends them one email).
          </p>
          <p className="mb-2">
            Hard exclusions still apply — manually picking someone who is unsubscribed will not override that. The
            picker shows a ⚠ next to unsubscribed people so you know.
          </p>
          <p className="mb-2">
            <strong className="text-white">Triggered campaigns ignore manual picks</strong> — they use the conditions
            filter to auto-detect new matches every hour. Use a "Send Now" or "Send Later" schedule for one-shot
            sends to specific people.
          </p>
        </section>

        <section>
          <h3 className="text-gold font-medium mb-2">Hard Exclusions (Always Applied)</h3>
          <p className="mb-1">These recipients are <strong className="text-red-400">always excluded</strong> regardless of filters:</p>
          <p className="mb-1">• <code className="text-white bg-royal-800 px-1 rounded">users.marketing_emails_unsubscribe = true</code> — User clicked unsubscribe in a marketing email</p>
          <p className="mb-1">• <code className="text-white bg-royal-800 px-1 rounded">sales_leads.unsubscribed = true</code> — Sales lead clicked unsubscribe</p>
          <p className="mt-2 text-royal-400">These cannot be overridden by admin. The only way to re-include them is for the user to re-subscribe (not currently supported — by design).</p>
        </section>

        <section>
          <h3 className="text-gold font-medium mb-2">Column Types & Operators</h3>
          <p className="mb-1"><strong className="text-white">Boolean columns</strong> (ispaid, isadmin, emailverified, etc.): Use "Is True" or "Is False"</p>
          <p className="mb-1"><strong className="text-white">Timestamp columns</strong> (createdat, trial_expires_at, etc.): Use "Older Than X days/weeks/months", "Newer Than", "Before (date)", "After (date)", "Is Null", "Is Not Null"</p>
          <p className="mb-1"><strong className="text-white">Text columns</strong> (email, stripecustomerid, etc.): Use "Equals", "Contains", "Is Null", "Is Not Null"</p>
          <p className="mb-1"><strong className="text-white">Number columns</strong> (usertype, freeplandeliveriestotalusage, etc.): Use =, &gt;, &lt;, ≥, ≤</p>
          <p className="mb-1"><strong className="text-white">Aggregates</strong> (sent_count, recipient_count): These count data across related tables — same numeric operators</p>
        </section>

        <section>
          <h3 className="text-gold font-medium mb-2">Key Column Reference</h3>
          <p className="mb-1"><code className="text-white bg-royal-800 px-1 rounded">users.usertype</code> — The user type ID (1=Free, 3=Fairydust, 4=Business Wizard). Join with user_types for plan details.</p>
          <p className="mb-1"><code className="text-white bg-royal-800 px-1 rounded">users.stripecustomerid</code> — If NULL, user has never been a paying customer. If NOT NULL, they have (or had) a Stripe account.</p>
          <p className="mb-1"><code className="text-white bg-royal-800 px-1 rounded">users.trial_expires_at</code> — If set and in the past, their trial has ended. If NULL, no trial was ever started.</p>
          <p className="mb-1"><code className="text-white bg-royal-800 px-1 rounded">users.gift_expires_at</code> — If set and in the future, they are on an active gift subscription.</p>
          <p className="mb-1"><code className="text-white bg-royal-800 px-1 rounded">users.freeplandeliveriestotalusage</code> — Lifetime count of deliveries used on the free plan.</p>
          <p className="mb-1"><code className="text-white bg-royal-800 px-1 rounded">user_types.ispaid</code> — Whether the user's current plan is a paid tier (true) or free (false).</p>
          <p className="mb-1"><code className="text-white bg-royal-800 px-1 rounded">moonbeam_recipients.recipient_count</code> — Total people this user has delivered eCards to (across all cards). Primary engagement metric. A user who sent 1 card to 100 people = 100.</p>
          <p className="mb-1"><code className="text-white bg-royal-800 px-1 rounded">moonbeams.sent_count</code> — Total number of eCards this user has created and sent.</p>
        </section>

        <section>
          <h3 className="text-gold font-medium mb-2">Reward Types — Limitations</h3>
          <p className="mb-1"><strong className="text-white">Special Access rewards</strong> only apply to registered users (they need a user account to view special templates). If your audience includes sales leads, they will receive the email but will NOT get access grants. This is expected — the email might encourage them to sign up.</p>
          <p className="mb-1"><strong className="text-white">Gift Code rewards</strong> work for both users and sales leads. Sales leads would need to create an account to redeem the code, but the code itself is valid for anyone.</p>
          <p className="mb-1"><strong className="text-white">Text rewards</strong> are purely informational and work for everyone.</p>
        </section>

        <section>
          <h3 className="text-gold font-medium mb-2">Email Templates — Whole-Email Model</h3>
          <p className="mb-2">
            Each template in the <strong className="text-white">Email Templates</strong> tab is a <strong className="text-gold">whole email</strong> —
            header markup, body content, unsubscribe link, and footer all together in one HTML blob. There are two kinds:
          </p>
          <p className="mb-1">• <strong className="text-white">Body Templates</strong> — for informational campaigns (no reward). The email is sent as-is.</p>
          <p className="mb-1">• <strong className="text-white">Reward Templates</strong> — one per reward type (text / access / giftcode). The HTML includes reward placeholders inline ({'{{gift_code}}'}, {'{{access_card_list}}'}, etc.) which get filled at send time.</p>
          <p className="mb-2 mt-2">
            When creating a campaign, the email field is pre-filled by picking a template, then can be tweaked per-campaign without affecting the saved template.
            For reward campaigns, the <strong className="text-white">Reward Data</strong> block (gift plan, access card list, reward text) supplies the values that fill the placeholders in the chosen template.
          </p>
        </section>

        <section>
          <h3 className="text-gold font-medium mb-2">Unsubscribe System</h3>
          <p className="mb-2">
            Marketing emails use a <strong className="text-white">separate</strong> unsubscribe system from eCard notification emails.
            The existing <code className="text-white bg-royal-800 px-1 rounded">unsubscribers</code> table is for per-sender eCard opt-outs
            (recipient doesn't want cards from a specific sender). Marketing unsubscribe is a global opt-out stored on
            <code className="text-white bg-royal-800 px-1 rounded">users.marketing_emails_unsubscribe</code> or <code className="text-white bg-royal-800 px-1 rounded">sales_leads.unsubscribed</code>.
          </p>
        </section>

        <section>
          <h3 className="text-gold font-medium mb-2">Rate Limiting & Large Audiences</h3>
          <p className="mb-2">
            Emails are sent by a background job that runs every 1–2 minutes. It processes up to <strong className="text-white">50 recipients per tick</strong> (configurable in Settings)
            with a <strong className="text-white">1 email per second</strong> rate limit (also configurable). For a campaign with 1,000 recipients, expect ~20 minutes for full delivery.
            This is designed to stay within Resend's free tier limits. You can monitor progress in the Recipients view.
          </p>
        </section>

        <section>
          <h3 className="text-gold font-medium mb-2">Drip Series</h3>
          <p className="mb-2">
            A drip series sends multiple emails over time. Each email has a <strong className="text-white">delay_days</strong> value —
            how many days after the campaign starts before that email is sent. The background job checks daily and unlocks the next
            email in the sequence once the delay has elapsed and all previous emails have finished sending.
          </p>
        </section>

        <section>
          <h3 className="text-gold font-medium mb-2">Triggered / Automated Campaigns</h3>
          <p className="mb-2">
            Triggered campaigns stay active and <strong className="text-white">automatically find new recipients every hour</strong>.
            Anyone who newly matches your audience filter and hasn't already been emailed gets queued and sent to.
            Each person only receives the email once per campaign.
          </p>
          <p className="mb-2">
            <strong className="text-white">Start Watching</strong> — Only people created after this date are considered.
            This prevents emailing your entire existing user base when you create a new triggered campaign.
            For example, a welcome email campaign started today will only send to users who sign up from today onward.
            Leave blank to include everyone who currently matches (useful for milestone rewards like "1000+ deliveries").
          </p>
          <p className="mb-2">
            <strong className="text-white">Stop Watching</strong> — The campaign automatically completes on this date.
            Leave blank to run until you manually cancel it.
          </p>
          <p className="mb-2">
            <strong className="text-white">Manual selection is disabled</strong> for triggered campaigns. They run on conditions only.
          </p>
        </section>

        <section>
          <h3 className="text-gold font-medium mb-2">Common Campaign Examples</h3>
          <p className="mb-1 text-royal-300">1. <strong className="text-white">Re-engage expired trial users:</strong> users.trial_expires_at older_than 2 weeks AND user_types.ispaid = false</p>
          <p className="mb-1 text-royal-300">2. <strong className="text-white">Reward top senders:</strong> moonbeam_recipients.recipient_count ≥ 1000</p>
          <p className="mb-1 text-royal-300">3. <strong className="text-white">Win back former customers:</strong> users.stripecustomerid is_not_null AND user_types.ispaid = false</p>
          <p className="mb-1 text-royal-300">4. <strong className="text-white">New user welcome drip:</strong> users.createdat newer_than 1 day (series with 3 emails over 2 weeks)</p>
          <p className="mb-1 text-royal-300">5. <strong className="text-white">Holiday access campaign:</strong> audience = all active users, reward = special access to holiday templates Nov 25 – Jan 1</p>
          <p className="mt-3 mb-1 text-purple-300 text-xs uppercase tracking-wider">Triggered campaign examples:</p>
          <p className="mb-1 text-royal-300">6. <strong className="text-white">Welcome email (triggered):</strong> filter = users.emailverified is_true, start = today, no end date. Every new verified user gets the welcome email automatically.</p>
          <p className="mb-1 text-royal-300">7. <strong className="text-white">Milestone reward (triggered):</strong> filter = moonbeam_recipients.recipient_count ≥ 500, start = today, no end date. Anyone who crosses 500 deliveries gets a congratulations + gift code.</p>
          <p className="mb-1 text-royal-300">8. <strong className="text-white">Trial expiry nudge (triggered):</strong> filter = users.trial_expires_at older_than 3 days AND user_types.ispaid = false, start = today. Catches every user 3 days after their trial ends.</p>
        </section>
      </div>

      <div className="flex justify-end mt-4">
        <Button variant="outline" onClick={onClose}>Close</Button>
      </div>
    </Modal>
  );
}


// ════════════════════════════════════════════════════════════
// SHARED UI COMPONENTS
// ════════════════════════════════════════════════════════════

function FormField({ label, children }) {
  return (
    <div>
      <label className="block text-royal-300 text-xs mb-1">{label}</label>
      {children}
    </div>
  );
}

function SmallBtn({ onClick, label, variant = 'default' }) {
  const colors = {
    default: 'border-royal-600 text-royal-200 hover:border-gold/50 hover:text-gold',
    green: 'border-green-700 text-green-400 hover:bg-green-900/30',
    red: 'border-red-800 text-red-400 hover:bg-red-900/30',
    yellow: 'border-yellow-700 text-yellow-400 hover:bg-yellow-900/30',
  };
  return (
    <button
      onClick={onClick}
      className={`px-2.5 py-1 text-xs border rounded transition-colors ${colors[variant]}`}
    >
      {label}
    </button>
  );
}

function LoadingSpinner() {
  return (
    <div className="flex items-center justify-center py-12">
      <div className="w-8 h-8 border-2 border-royal-600 border-t-gold rounded-full animate-spin" />
    </div>
  );
}

function EmptyState({ message }) {
  return (
    <div className="text-center py-12">
      <p className="text-royal-300 text-sm">{message}</p>
    </div>
  );
}

// ════════════════════════════════════════════════════════════
// REPLY EMAILS TAB
// ════════════════════════════════════════════════════════════

function ReplyEmailsTab() {
  const [replyEmails, setReplyEmails] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ email: '', display_name: '', is_default: false, is_active: true, notes: '' });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const loadReplyEmails = async () => {
    setLoading(true);
    try {
      const res = await adminService.getReplyEmails();
      setReplyEmails(res.replyEmails || []);
    } catch (err) {
      setError('Failed to load reply emails');
    }
    setLoading(false);
  };

  useEffect(() => { loadReplyEmails(); }, []);

  const resetForm = () => {
    setForm({ email: '', display_name: '', is_default: false, is_active: true, notes: '' });
    setEditing(null);
    setError('');
  };

  const handleEdit = (r) => {
    setEditing(r.replyemailid);
    setForm({
      email: r.email || '',
      display_name: r.display_name || '',
      is_default: r.is_default || false,
      is_active: r.is_active !== false,
      notes: r.notes || '',
    });
    setError('');
  };

  const handleSave = async () => {
    if (!form.email) {
      setError('Email is required');
      return;
    }

    setSaving(true);
    setError('');
    try {
      if (editing) {
        await adminService.updateReplyEmail(editing, form);
        setSuccess('Reply email updated');
      } else {
        await adminService.createReplyEmail(form);
        setSuccess('Reply email created');
      }
      resetForm();
      loadReplyEmails();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.data?.error || 'Save failed');
    }
    setSaving(false);
  };

  const handleDelete = async (r) => {
    if (!window.confirm(`Delete "${r.email}"? This cannot be undone.`)) return;

    try {
      await adminService.deleteReplyEmail(r.replyemailid);
      setSuccess('Reply email deleted');
      loadReplyEmails();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.data?.error || 'Delete failed');
    }
  };

  const handleSetDefault = async (r) => {
    try {
      await adminService.setDefaultReplyEmail(r.replyemailid);
      setSuccess('Default updated');
      loadReplyEmails();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.data?.error || 'Failed to set default');
    }
  };

  if (loading) return <LoadingSpinner />;

  return (
    <div className="space-y-6">
      {error && <div className="p-3 bg-red-900/50 border border-red-700 rounded-lg text-red-200 text-sm">{error}</div>}
      {success && <div className="p-3 bg-green-900/50 border border-green-700 rounded-lg text-green-200 text-sm">{success}</div>}

      <div className="bg-royal-900/50 border border-royal-700 rounded-lg p-4">
        <h3 className="text-gold font-medium mb-4">{editing ? 'Edit Reply Email' : 'Add Reply Email'}</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <FormField label="Email Address *">
            <input
              type="email"
              value={form.email}
              onChange={e => setForm({ ...form, email: e.target.value })}
              placeholder="support@example.com"
              className="w-full px-3 py-2 bg-royal-800/60 border border-royal-600 rounded-lg text-white text-sm"
            />
          </FormField>
          <FormField label="Display Name">
            <input
              type="text"
              value={form.display_name}
              onChange={e => setForm({ ...form, display_name: e.target.value })}
              placeholder="Customer Support"
              className="w-full px-3 py-2 bg-royal-800/60 border border-royal-600 rounded-lg text-white text-sm"
            />
          </FormField>
        </div>
        <div className="mt-4">
          <FormField label="Notes">
            <input
              type="text"
              value={form.notes}
              onChange={e => setForm({ ...form, notes: e.target.value })}
              placeholder="Optional notes about this email"
              className="w-full px-3 py-2 bg-royal-800/60 border border-royal-600 rounded-lg text-white text-sm"
            />
          </FormField>
        </div>
        <div className="flex items-center gap-6 mt-4">
          <label className="flex items-center gap-2 text-sm text-royal-200">
            <input
              type="checkbox"
              checked={form.is_default}
              onChange={e => setForm({ ...form, is_default: e.target.checked })}
              className="rounded border-royal-600"
            />
            Set as default
          </label>
          <label className="flex items-center gap-2 text-sm text-royal-200">
            <input
              type="checkbox"
              checked={form.is_active}
              onChange={e => setForm({ ...form, is_active: e.target.checked })}
              className="rounded border-royal-600"
            />
            Active
          </label>
        </div>
        <div className="flex items-center gap-3 mt-4">
          <Button onClick={handleSave} disabled={saving}>
            {saving ? 'Saving...' : editing ? 'Update' : 'Add Reply Email'}
          </Button>
          {editing && (
            <Button variant="outline" onClick={resetForm}>Cancel</Button>
          )}
        </div>
      </div>

      <div className="bg-royal-900/50 border border-royal-700 rounded-lg overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-royal-800/50">
            <tr>
              <th className="text-left px-4 py-3 text-royal-300 font-medium">Email</th>
              <th className="text-left px-4 py-3 text-royal-300 font-medium">Display Name</th>
              <th className="text-center px-4 py-3 text-royal-300 font-medium">Default</th>
              <th className="text-center px-4 py-3 text-royal-300 font-medium">Active</th>
              <th className="text-center px-4 py-3 text-royal-300 font-medium">Campaigns</th>
              <th className="text-right px-4 py-3 text-royal-300 font-medium">Actions</th>
            </tr>
          </thead>
          <tbody>
            {replyEmails.length === 0 ? (
              <tr>
                <td colSpan={6} className="text-center py-8 text-royal-400">
                  No reply emails yet. Add one above.
                </td>
              </tr>
            ) : (
              replyEmails.map(r => (
                <tr key={r.replyemailid} className="border-t border-royal-700/50 hover:bg-royal-800/30">
                  <td className="px-4 py-3 text-white">{r.email}</td>
                  <td className="px-4 py-3 text-royal-200">{r.display_name || '—'}</td>
                  <td className="px-4 py-3 text-center">
                    {r.is_default ? (
                      <span className="text-gold">★ Default</span>
                    ) : (
                      <button
                        onClick={() => handleSetDefault(r)}
                        className="text-royal-400 hover:text-gold text-xs"
                      >
                        Set default
                      </button>
                    )}
                  </td>
                  <td className="px-4 py-3 text-center">
                    {r.is_active ? (
                      <span className="text-green-400">✓</span>
                    ) : (
                      <span className="text-royal-500">—</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-center text-royal-300">
                    {r.campaign_count || 0}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <div className="flex items-center justify-end gap-2">
                      <SmallBtn label="Edit" onClick={() => handleEdit(r)} />
                      <SmallBtn label="Delete" variant="red" onClick={() => handleDelete(r)} />
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function SimplePagination({ page, totalPages, onPageChange }) {
  if (totalPages <= 1) return null;
  return (
    <div className="flex items-center justify-center gap-3 mt-6">
      <button
        onClick={() => onPageChange(Math.max(1, page - 1))}
        disabled={page <= 1}
        className="px-3 py-1.5 text-sm rounded border border-royal-600 text-royal-200 hover:border-gold/50 hover:text-gold disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
      >
        ← Prev
      </button>
      <span className="text-royal-300 text-sm">{page} / {totalPages}</span>
      <button
        onClick={() => onPageChange(Math.min(totalPages, page + 1))}
        disabled={page >= totalPages}
        className="px-3 py-1.5 text-sm rounded border border-royal-600 text-royal-200 hover:border-gold/50 hover:text-gold disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
      >
        Next →
      </button>
    </div>
  );
}